import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── CoachMark ────────────────────────────────────────────────────────────────

function CoachMark({ id, children }) {
  const key = `b360_hint_${id}`;
  const [visible, setVisible] = useState(() => !localStorage.getItem(key));

  function dismiss() {
    localStorage.setItem(key, '1');
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="coach-pulse" style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 14px',
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      marginBottom: 16,
    }}>
      <span style={{ fontSize: 13, color: 'var(--text-4)', flexShrink: 0, marginTop: 1 }}>✦</span>
      <p style={{
        flex: 1, margin: 0,
        fontFamily: 'var(--font-body)', fontSize: 13,
        color: 'var(--text-2)', lineHeight: 1.55,
      }}>
        {children}
      </p>
      <button
        onClick={dismiss}
        title="Dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-3)', fontSize: 16, lineHeight: 1,
          padding: '0 2px', flexShrink: 0, marginTop: -1,
        }}
      >
        ×
      </button>
    </div>
  );
}

// Workstream accent colors as CSS vars — automatically switch with theme
const WS_COLOR_VARS = {
  'Axon':            'var(--accent-axon)',
  'Finance':         'var(--accent-finance)',
  'Property':        'var(--accent-property)',
  'Home & Life':     'var(--accent-home)',
  'Growth & Career': 'var(--accent-growth)',
};

function wsColorHash(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (Math.imul(31, h) + name.charCodeAt(i)) | 0;
  const palette = ['#7A6E9F', '#5B7FA6', '#6E9A7A', '#9A6E7A', '#A08850', '#5A8A9A'];
  return palette[Math.abs(h) % palette.length];
}

function resolveColor(ws) {
  return ws.color || WS_COLOR_VARS[ws.name] || wsColorHash(ws.name);
}

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  return 'Good evening';
}

function taskAge(createdAt) {
  if (!createdAt) return null;
  const days = Math.floor((Date.now() - new Date(createdAt)) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1d';
  return `${days}d`;
}

// Reads stored preference or system preference; applies data-theme to <html>
function useThemeManager() {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('ceos-theme');
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    function handleChange(e) {
      if (!localStorage.getItem('ceos-theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    }
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  function toggleTheme() {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('ceos-theme', next);
      return next;
    });
  }

  return [theme, toggleTheme];
}

// ─── Energy Chip ──────────────────────────────────────────────────────────────

const ENERGY_COLORS = { H: '#4CAF82', M: '#E8A020', L: '#C0392B' };
const ENERGY_LABELS = { H: 'High', M: 'Medium', L: 'Low' };
const ENERGY_DESC = {
  H: 'I could take another meeting. Ideas are flowing. Proactive not reactive.',
  M: "Getting things done but not inspired. Functional. Wouldn't want more on my plate.",
  L: 'Running on fumes. Surviving the day. Need to protect my energy.',
};

// Relative "last logged: …" phrasing from an ISO timestamp.
function relTime(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// One filled/empty dot in the 7-day history strip.
function StripDot({ day, isToday, backfillable, active, onClick }) {
  const color = day.state ? ENERGY_COLORS[day.state] : null;
  const weekday = new Date(day.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
  const title = day.state
    ? `${weekday}: ${ENERGY_LABELS[day.state]}`
    : backfillable ? `${weekday}: not logged — tap to add` : `${weekday}: not logged`;
  return (
    <button
      onClick={backfillable ? onClick : undefined}
      disabled={!backfillable}
      title={title}
      style={{
        width: 18, height: 18, borderRadius: '50%', padding: 0,
        background: color || 'transparent',
        border: color
          ? `2px solid ${color}`
          : `2px ${backfillable ? 'dashed' : 'solid'} ${active ? 'var(--text-2)' : 'var(--text-4)'}`,
        boxShadow: isToday ? '0 0 0 2px var(--surface), 0 0 0 3.5px var(--text-3)' : 'none',
        cursor: backfillable ? 'pointer' : 'default',
        opacity: !day.state && !backfillable ? 0.55 : 1,
        flexShrink: 0,
      }}
    />
  );
}

function EnergyBanner({ energy, onLogged, onLog, onOpenCapture }) {
  const [logging, setLogging] = useState(false);
  // Which past day is being backfilled (date string), or null when logging today.
  const [backfillDate, setBackfillDate] = useState(null);

  const current = energy?.current || null;
  const strip = energy?.strip || [];
  const today = energy?.today || null;
  const color = current ? ENERGY_COLORS[current] : null;
  const lastLogged = relTime(energy?.loggedAt);

  async function handleSelect(state, date) {
    if (logging) return;
    setLogging(true);
    try {
      const r = await fetch('/api/energy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(date ? { state, date } : { state }),
      });
      if (r.ok) {
        const d = await r.json();
        onLogged?.(d);
        // Only narrate live (today) logs in chat — backfills are silent.
        if (!date || date === today) onLog?.(state, current, energy?.loggedAt);
      }
    } finally {
      setLogging(false);
      setBackfillDate(null);
    }
  }

  // Selector targets today by default, or the day being backfilled.
  const selectorDate = backfillDate || today;
  const selectorWeekday = backfillDate
    ? new Date(backfillDate + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long' })
    : null;

  return (
    <div style={{
      margin: '0 0 12px',
      padding: '11px 14px',
      background: current ? `${color}10` : 'var(--surface)',
      border: `1px solid ${current ? `${color}35` : 'var(--border)'}`,
      borderLeft: `3px solid ${current ? color : 'var(--border)'}`,
      borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* State indicator — today only; always neutral until today is logged */}
        {current ? (
          <span style={{
            width: 26, height: 26, borderRadius: '50%',
            background: color, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, flexShrink: 0,
          }}>{current}</span>
        ) : (
          <span style={{
            width: 26, height: 26, borderRadius: '50%',
            border: '1.5px dashed var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: 14, color: 'var(--text-4)', lineHeight: 1,
          }}>·</span>
        )}

        {/* Label + description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 600, color: current ? 'var(--text-2)' : 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 2px' }}>
            {backfillDate
              ? `Energy — ${selectorWeekday}`
              : `Energy${current ? ` — ${ENERGY_LABELS[current]}` : ''}`}
            {!backfillDate && current && lastLogged && (
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-4)' }}>
                {' · last logged: '}{lastLogged}
              </span>
            )}
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: current && !backfillDate ? 'var(--text)' : 'var(--text-4)', margin: 0, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {backfillDate
              ? `How were you running on ${selectorWeekday}?`
              : current ? ENERGY_DESC[current] : 'How are you running today? Log your energy level.'}
          </p>
        </div>

        {/* Inline H / M / L selector — targets today, or the day being backfilled */}
        <div style={{ display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center' }}>
          {['H', 'M', 'L'].map(s => {
            const selected = !backfillDate && current === s;
            return (
              <button
                key={s}
                onClick={() => handleSelect(s, backfillDate || undefined)}
                disabled={logging}
                title={`${ENERGY_LABELS[s]} — ${ENERGY_DESC[s]}`}
                style={{
                  padding: '5px 11px', borderRadius: 6,
                  background: 'none',
                  border: `1px solid ${selected ? '#5B7FA6' : '#D0CCC6'}`,
                  color: selected ? '#5B7FA6' : 'var(--text-2)',
                  fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500, lineHeight: 1,
                  cursor: logging ? 'default' : 'pointer',
                  transition: 'border-color .12s, color .12s',
                  opacity: logging ? 0.5 : 1,
                }}
              >
                {s}
              </button>
            );
          })}
          {backfillDate && (
            <button
              onClick={() => setBackfillDate(null)}
              title="Cancel"
              style={{
                marginLeft: 2, background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-4)', fontSize: 16, lineHeight: 1, padding: '0 2px',
              }}
            >×</button>
          )}
        </div>
      </div>

      {/* 7-dot history strip — fill rate is the confidence signal */}
      {strip.length === 7 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, paddingLeft: 38 }}>
          <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
            {strip.map(day => {
              const isToday = day.date === today;
              const backfillable = !day.state && today != null && day.date <= today && !logging;
              return (
                <StripDot
                  key={day.date}
                  day={day}
                  isToday={isToday}
                  backfillable={backfillable}
                  active={backfillDate === day.date}
                  onClick={() => setBackfillDate(isToday ? null : day.date)}
                />
              );
            })}
          </div>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)', marginLeft: 4, letterSpacing: '0.01em' }}>
            {strip.filter(d => d.state).length} of 7 logged
          </span>
        </div>
      )}

      {onOpenCapture && (
        <HandoverPill prominent={current === 'L'} onOpen={onOpenCapture} />
      )}
    </div>
  );
}

// ─── "Hand this over" trigger pill (anxiety-reduction-brief.md) ────────────────
// Two parts: open the capture card in chat, or dictate straight into it. Always present;
// at low energy it gets stronger weight and an empathetic lead-in line.
function HandoverPill({ prominent, onOpen }) {
  const bufRef = useRef('');
  const { listening, supported, toggle } = useDictation((chunk) => {
    bufRef.current = bufRef.current ? bufRef.current + ' ' + chunk : chunk;
  });

  function micClick() {
    if (!supported) { onOpen(''); return; }
    if (listening) {
      toggle(); // stop
      onOpen(bufRef.current.trim());
      bufRef.current = '';
    } else {
      bufRef.current = '';
      toggle(); // start
    }
  }

  return (
    <div style={{ marginTop: 12, paddingLeft: 38 }}>
      {prominent && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-2)', margin: '0 0 7px', lineHeight: 1.45 }}>
          Feeling low today? Let's dissect what you're carrying.
        </p>
      )}
      <div style={{ display: 'inline-flex', alignItems: 'stretch', borderRadius: 999, overflow: 'hidden', border: `1px solid ${prominent ? 'var(--text-2)' : 'var(--border)'}`, background: prominent ? 'var(--surface)' : 'transparent' }}>
        <button
          onClick={() => onOpen('')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 13px',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontSize: 12,
            fontWeight: prominent ? 600 : 500,
            color: prominent ? 'var(--text)' : 'var(--text-2)',
          }}
        >
          <span style={{ fontSize: 13 }}>✦</span>
          Hand something over
        </button>
        <button
          onClick={micClick}
          aria-label={listening ? 'Stop dictation' : 'Dictate'}
          title={supported ? (listening ? 'Listening… tap to finish' : 'Dictate') : 'Dictation not supported in this browser'}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '6px 11px', borderLeft: `1px solid ${prominent ? 'var(--text-2)35' : 'var(--border)'}`,
            background: listening ? 'var(--danger)' : 'none', border: 'none',
            cursor: 'pointer', color: listening ? '#fff' : (prominent ? 'var(--text)' : 'var(--text-2)'),
            fontSize: 13,
          }}
        >
          {listening ? '● Listening…' : '🎙'}
        </button>
      </div>
    </div>
  );
}

// Web Speech dictation (Chrome/Safari). `onChunk` receives each finalized transcript
// segment. Mirrors the onboarding brain-dump pattern; shared by the handover card + pill.
function useDictation(onChunk) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const onChunkRef = useRef(onChunk);
  onChunkRef.current = onChunk;
  const supported = typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggle = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (e) => {
      const transcript = Array.from(e.results)
        .slice(e.resultIndex)
        .filter(r => r.isFinal)
        .map(r => r[0].transcript.trim())
        .join(' ');
      if (transcript) onChunkRef.current?.(transcript);
    };
    recognition.onend = () => { recognitionRef.current = null; setListening(false); };
    recognition.onerror = () => { recognitionRef.current = null; setListening(false); };
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  }, []);

  useEffect(() => () => recognitionRef.current?.stop(), []);
  return { listening, supported, toggle };
}

function EnergyChip({ current, onLog }) {
  const [open, setOpen] = useState(false);
  const [logging, setLogging] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSelect(state) {
    if (logging) return;
    setLogging(true);
    setOpen(false);
    try {
      await fetch('/api/energy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state }),
      });
      onLog(state);
    } finally {
      setLogging(false);
    }
  }

  const color = current ? ENERGY_COLORS[current] : null;

  return (
    <div ref={ref} style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 90 }}>
      {open && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 6, width: 240,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}>
          {['H', 'M', 'L'].map(s => (
            <button
              key={s}
              onClick={() => handleSelect(s)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                width: '100%', padding: '9px 10px', borderRadius: 7,
                background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                background: ENERGY_COLORS[s], color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, marginTop: 1,
              }}>{s}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>
                  {ENERGY_LABELS[s]}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4 }}>
                  {ENERGY_DESC[s]}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        title={current ? `Energy: ${ENERGY_LABELS[current]} — tap to update` : 'Log your energy level'}
        style={{
          width: 36, height: 36, borderRadius: '50%',
          background: color || 'var(--surface)',
          border: `1.5px solid ${color || 'var(--border)'}`,
          color: color ? '#fff' : 'var(--text-3)',
          fontSize: 11, fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background .15s, border-color .15s',
        }}
      >
        {logging ? '…' : (current || '·')}
      </button>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

// Tracks whether the viewport is mobile-width. Drives the Briefing's per-device layout:
// rows + tap-through detail on mobile, the card grid on desktop.
function useIsMobile(maxWidth = 768) {
  const query = `(max-width: ${maxWidth}px)`;
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [query]);
  return isMobile;
}

// Viewport >= minWidth (desktop). Drives the two-column layout + persistent chat rail.
function useIsDesktop(minWidth = 1100) {
  const query = `(min-width: ${minWidth}px)`;
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [query]);
  return isDesktop;
}

export default function Dashboard({ chatProps }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('briefing');
  const [mobileChatVisible, setMobileChatVisible] = useState(false);
  const [detailWsId, setDetailWsId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  // Chat-rail width is user-resizable on desktop. Persisted in localStorage per explicit
  // request (overrides the general "no localStorage" rule); guarded so it degrades safely.
  const [railWidth, setRailWidth] = useState(() => {
    try { const v = parseInt(localStorage.getItem('bearing360_panel_width'), 10); if (v >= 240 && v <= 480) return v; } catch (_) {}
    return 300;
  });
  const [energy, setEnergy] = useState({ current: null, loggedAt: null, strip: [], today: null });

  const {
    chatMessages, setChatMessages,
    chatSessionId, setChatSessionId,
    pendingAction, setPendingAction,
    anxietyCardOpen, setAnxietyCardOpen,
    anxietyPrefill, setAnxietyPrefill,
    handleLogout,
    userName,
    openFeedback,
  } = chatProps;

  // Open the "hand this over" card in the chat thread. `prefill` seeds the first field
  // (used by the EnergyBanner mic, which dictates straight into the card).
  const openAnxietyCard = useCallback((prefill = '') => {
    setAnxietyPrefill(prefill || '');
    setAnxietyCardOpen(true);
    setMobileChatVisible(true); // bring the chat rail into view on mobile (no-op on desktop)
  }, [setAnxietyPrefill, setAnxietyCardOpen]);

  const [undoTask, setUndoTask] = useState(null);
  // The single most-recently-completed task, kept rendered (struck-through) in its card
  // until the next completion / undo / refresh. Transient by design (frontend-only).
  const [lastCompleted, setLastCompleted] = useState(null);
  // Watch / Parked / Priority (Tier 3, 2026-07-22): segmented control on the Briefing grid,
  // "By Workstream" (existing grouped view, default) vs "By Priority" (flat, cross-workstream).
  const [gridMode, setGridMode] = useState('workstream');
  const undoTimerRef = useRef(null);

  // Best-effort context for the Feedback widget (isolated; read on submit only). Reports
  // the active tab and, on the Memories tab, which arenas are expanded (read from the DOM
  // so this stays decoupled from MemoriesPane's internal state).
  useEffect(() => {
    window.__b360FeedbackContext = () => {
      const ctx = { tab: view };
      try {
        if (view === 'memories') {
          ctx.expandedArenas = [...document.querySelectorAll('.memories-left [aria-expanded="true"]')]
            .map(el => el.textContent.trim().replace(/\s+/g, ' '))
            .slice(0, 12);
        }
      } catch (_) { /* best-effort */ }
      return ctx;
    };
    return () => { delete window.__b360FeedbackContext; };
  }, [view]);

  const fetchState = useCallback(async () => {
    try {
      const r = await fetch('/api/state');
      if (r.ok) setState(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  useEffect(() => {
    fetch('/api/energy')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setEnergy({ current: d.current, loggedAt: d.loggedAt, strip: d.strip, today: d.today, weekStart: d.weekStart, weekEnd: d.weekEnd });
      })
      .catch(() => {});
  }, []);

  // A write (live or backfill) returns today's state + the refreshed strip.
  function handleEnergyLogged(d) {
    setEnergy(prev => ({ ...prev, current: d.current, loggedAt: d.loggedAt, strip: d.strip }));
  }

  // Narrate live (today) logs in chat — backfills stay silent.
  const ENERGY_RANK = { H: 3, M: 2, L: 1 };
  function handleEnergyLog(state, prevState, prevLoggedAt) {
    const isDownward = prevState && ENERGY_RANK[state] < ENERGY_RANK[prevState];
    const elapsedMs = prevLoggedAt ? Date.now() - new Date(prevLoggedAt).getTime() : 0;
    const pastWindow = elapsedMs >= 60 * 60 * 1000;

    const plainMsg = {
      H: "Energy: High — ideas are flowing. Operating proactively today.",
      M: "Energy: Medium — getting things done, but not at peak. Functional.",
      L: "Energy: Low — running on fumes. Protecting your energy today.",
    }[state];
    const dropMsg = {
      M: "Energy dropped to Medium since this morning — what changed, if anything? No need to answer if you'd rather not.",
      L: "Energy dropped to Low — what made it drain faster today? Totally fine to skip this too.",
    }[state];

    const msg = (isDownward && pastWindow) ? (dropMsg || plainMsg) : plainMsg;
    if (msg) setChatMessages(prev => [...prev, { role: 'assistant', content: msg, fromUI: true }]);
  }

  function showUndoToast({ taskId, taskText, kind }) {
    clearTimeout(undoTimerRef.current);
    setUndoTask({ taskId, taskText, kind });
    undoTimerRef.current = setTimeout(() => setUndoTask(null), 5000);
  }

  function handleTaskComplete({ task, workstreamId }) {
    // Keep the just-completed task struck-through in place (replaces any prior one).
    setLastCompleted({ ...task, workstreamId });
    showUndoToast({ taskId: task.id, taskText: task.text, kind: 'complete' });
    fetchState();
  }

  function handleTaskDelete({ task }) {
    showUndoToast({ taskId: task.id, taskText: task.text, kind: 'delete' });
    fetchState();
  }

  // Restore a task to 'active' — undoes both a complete and a soft-delete (both the
  // toast Undo and clicking the struck-through circle route here).
  const restoreTask = useCallback(async (taskId) => {
    clearTimeout(undoTimerRef.current);
    setUndoTask(u => (u && u.taskId === taskId ? null : u));
    setLastCompleted(lc => (lc && lc.id === taskId ? null : lc));
    try {
      await fetch('/api/state/tasks/undo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId }),
      });
    } catch (_) { /* best-effort; state refetch reconciles */ }
    fetchState();
  }, [fetchState]);

  function handleUndo() {
    if (undoTask) restoreTask(undoTask.taskId);
  }

  // Persist a new manual order for one workstream. Optimistic: reorder local state
  // immediately, then POST; on failure, refetch to reconcile.
  const handleReorder = useCallback(async (workstreamId, orderedIds) => {
    setState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        workstreams: prev.workstreams.map(ws => {
          if (ws.id !== workstreamId) return ws;
          const byId = Object.fromEntries((ws.tasks || []).map(t => [t.id, t]));
          const reordered = orderedIds.map(id => byId[id]).filter(Boolean);
          const leftover = (ws.tasks || []).filter(t => !orderedIds.includes(t.id));
          return { ...ws, tasks: [...reordered, ...leftover] };
        }),
      };
    });
    try {
      await fetch('/api/state/tasks/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workstreamId, orderedIds }),
      });
    } catch (_) {
      fetchState();
    }
  }, [fetchState]);

  useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  function handleToggle(v) {
    if (v === 'chat') {
      setMobileChatVisible(true);
    } else {
      setMobileChatVisible(false);
      setView(v);
    }
  }

  if (loading) return <PageLoader />;

  const workstreams = state?.workstreams || [];
  const pulse = state?.pulse || {};
  const detailWs = isMobile && detailWsId ? workstreams.find(w => w.id === detailWsId) : null;
  const goView = (v) => { setDetailWsId(null); handleToggle(v); };

  // Drag the rail's left edge to resize (240–480px), persisted on mouse-up.
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = railWidth;
    let latest = startW;
    const onMove = (ev) => {
      latest = Math.min(480, Math.max(240, startW + (startX - ev.clientX)));
      setRailWidth(latest);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { localStorage.setItem('bearing360_panel_width', String(latest)); } catch (_) {}
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const chatPane = (
    <ChatPane
      messages={chatMessages}
      setMessages={setChatMessages}
      sessionId={chatSessionId}
      setSessionId={setChatSessionId}
      pendingAction={pendingAction}
      setPendingAction={setPendingAction}
      anxietyCardOpen={anxietyCardOpen}
      setAnxietyCardOpen={setAnxietyCardOpen}
      anxietyPrefill={anxietyPrefill}
      onStateChange={fetchState}
      mobileVisible={mobileChatVisible}
      onClose={() => setMobileChatVisible(false)}
      desktop={isDesktop}
      railWidth={railWidth}
    />
  );

  return (
    <>
      <div className="app-shell" style={{
        height: '100dvh', display: 'flex', flexDirection: isDesktop ? 'row' : 'column',
        overflow: 'hidden', background: 'var(--stone-bg)', position: 'relative',
      }}>
        <div className="main-col" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        {detailWs ? (
          <WorkstreamDetail
            workstream={detailWs}
            onBack={() => setDetailWsId(null)}
            onTaskComplete={handleTaskComplete}
            onTaskDelete={handleTaskDelete}
            onUndo={restoreTask}
            onChanged={fetchState}
            onReorder={handleReorder}
            lastCompleted={lastCompleted}
          />
        ) : view === 'reflection' ? (
          <>
            <div style={{
              flexShrink: 0, height: 44, display: 'flex', alignItems: 'center',
              justifyContent: 'flex-end', padding: '0 16px', gap: 6,
              borderBottom: '1px solid var(--stone-border)',
            }}>
              <NavToggle view={view} onView={goView} />
              <GearButton onClick={() => setSettingsOpen(true)} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <ReflectionPane />
            </div>
          </>
        ) : (
          <>
            <SunriseBand userName={userName} view={view} onView={goView} onOpenSettings={() => setSettingsOpen(true)} />
            <div className="brief-scroll" style={{ flex: 1, overflowY: 'auto' }}>
              {/* Single content column owns the horizontal rhythm; children add no
                  horizontal padding of their own, so all left/right edges align. */}
              <div className="brief-col content-col" style={{ paddingTop: 18, paddingBottom: 56 }}>
                <EnergyBanner energy={energy} onLogged={handleEnergyLogged} onLog={handleEnergyLog} onOpenCapture={openAnxietyCard} />
                <ReflectInvitationCard onReflect={() => goView('reflection')} />
                <ProposalsCards workstreams={workstreams} onResolved={fetchState} />
                {isMobile ? (
                  <WorkstreamRows workstreams={workstreams} onOpen={setDetailWsId} />
                ) : (
                  <div style={{ padding: '4px 0 8px' }}>
                    {/* Watch / Parked / Priority (Tier 3, 2026-07-22): segmented control on the
                        existing grid — no new page, no third top-level mode. */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                      <GridModeToggle mode={gridMode} onChange={setGridMode} />
                    </div>
                    {gridMode === 'priority' ? (
                      <PriorityBoard
                        workstreams={workstreams}
                        priorityOrder={state?.priorityOrder || []}
                        onTaskComplete={handleTaskComplete}
                        onTaskDelete={handleTaskDelete}
                        onUndo={restoreTask}
                        onChanged={fetchState}
                        lastCompleted={lastCompleted}
                      />
                    ) : (
                      <WorkstreamGrid
                        workstreams={workstreams}
                        onTaskComplete={handleTaskComplete}
                        onTaskDelete={handleTaskDelete}
                        onUndo={restoreTask}
                        onChanged={fetchState}
                        onReorder={handleReorder}
                        lastCompleted={lastCompleted}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            {!isDesktop && (
              <div style={{ flexShrink: 0, minHeight: 52, display: 'flex', alignItems: 'center', borderTop: '1px solid var(--stone-border)', background: 'var(--surface)' }}>
                <div className="content-col" style={{ paddingTop: 8, paddingBottom: 8 }}>
                  <CaptureBar onOpen={() => setMobileChatVisible(true)} />
                </div>
              </div>
            )}
          </>
        )}

        {undoTask && (
          <div className="fade-in" style={{
            position: 'absolute', bottom: 76, left: 16, right: 16,
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 40,
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-2)', flex: 1, fontFamily: 'var(--font-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {undoTask.kind === 'delete' ? 'Deleted' : '✓'} <span style={{ color: 'var(--text-3)' }}>{undoTask.taskText}</span>
            </span>
            <button onClick={handleUndo} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', color: 'var(--text)', fontSize: 12, fontFamily: 'var(--font-body)', padding: '3px 10px', flexShrink: 0 }}>Undo</button>
            <button onClick={() => { clearTimeout(undoTimerRef.current); setUndoTask(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16, padding: '0 2px', flexShrink: 0 }}>×</button>
          </div>
        )}
        </div>{/* main-col */}
        {isDesktop && <div className="resize-handle" onMouseDown={startResize} title="Drag to resize" />}
        {isDesktop && chatPane}
      </div>

      {!isDesktop && (
        <>
          {mobileChatVisible && (
            <div className="chat-backdrop" onClick={() => setMobileChatVisible(false)} />
          )}
          {chatPane}
        </>
      )}
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} onLogout={handleLogout} onFeedback={openFeedback} />
    </>
  );
}

// ─── Workstream Grid ──────────────────────────────────────────────────────────

// === Bold Briefing (Sprint 2) =================================================
// Time-of-day sunrise gradient (design/sprint-2/README.md).
function timeGradient() {
  const h = new Date().getHours();
  if (h >= 5 && h < 11)  return 'linear-gradient(170deg, #5A7894 0%, #84A0B6 18%, #AAA08E 40%, #C6B898 58%, #D8CAAE 78%, #E4D8C2 100%)';
  if (h >= 11 && h < 17) return 'linear-gradient(170deg, #7A9DB8 0%, #98B6C8 20%, #B6C2AE 45%, #CCBFA0 65%, #DCCEB6 85%, #E8E2CC 100%)';
  if (h >= 17 && h < 21) return 'linear-gradient(170deg, #7A5470 0%, #9C6878 22%, #C08C6A 46%, #D4AE8A 66%, #DECCB0 84%, #E8E2D4 100%)';
  return 'linear-gradient(170deg, #18202E 0%, #242C40 20%, #2E3650 40%, #383E5C 60%, #424462 80%, #565064 100%)';
}

function GrainOverlay({ opacity = 0.10 }) {
  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity, mixBlendMode: 'overlay', pointerEvents: 'none' }} xmlns="http://www.w3.org/2000/svg">
      <defs><filter id="b360grain"><feTurbulence type="fractalNoise" baseFrequency="0.72" numOctaves="4" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter></defs>
      <rect width="100%" height="100%" filter="url(#b360grain)"/>
    </svg>
  );
}

// Briefing/Reflect pill toggle. onGradient = translucent-white variant for the sunrise band.
function NavToggle({ view, onView, onGradient }) {
  const wrap = onGradient
    ? { background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.20)' }
    : { background: '#E4E0D9', border: 'none' };
  const active = onGradient
    ? { color: 'rgba(255,255,255,0.95)', background: 'rgba(255,255,255,0.20)' }
    : { color: '#fff', background: '#5B7FA6' };
  const idle = onGradient ? { color: 'rgba(255,255,255,0.55)' } : { color: '#A8A49F' };
  const btn = (v, label) => (
    <button onClick={() => onView(v)} style={{
      fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: 1, whiteSpace: 'nowrap',
      padding: '5px 13px', borderRadius: 20, border: 'none', cursor: 'pointer',
      fontWeight: view === v ? 500 : 400, ...(view === v ? active : idle),
    }}>{label}</button>
  );
  return (
    <div style={{ display: 'flex', borderRadius: 24, padding: 3, ...wrap }}>
      {btn('briefing', 'Briefing')}
      {btn('reflection', 'Reflect')}
    </div>
  );
}

// Sunrise band: greeting + nav toggle over the time-of-day gradient. Replaces AppHeader on
// the Briefing.
// Small gear button. `light` renders a white icon for the gradient hero; otherwise dark.
function GearButton({ onClick, light }) {
  const stroke = light ? 'rgba(255,255,255,0.85)' : 'var(--text-3)';
  return (
    <button onClick={onClick} aria-label="Settings" style={{
      width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer',
      background: light ? 'rgba(255,255,255,0.14)' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </button>
  );
}

// ─── Settings (Reflect v2): review-rhythm picker (artboard 06) ─────────────────
// Minimal account surface. Phase 1 ships the review-rhythm control; timezone + the Memory
// inspection home are reserved slots for a later pass.
const REVIEW_RHYTHMS = [
  { value: 'friday', label: 'Friday close', sub: 'Available Friday afternoon. Wrap the week before the weekend.' },
  { value: 'sunday', label: 'Sunday reset', sub: 'Available Sunday. Quiet reflection before the week resets.', isDefault: true },
  { value: 'monday', label: 'Monday morning', sub: 'Available Monday. Orientation for the week ahead, not a look back.' },
  { value: 'none', label: 'No scheduled prompt', sub: "Show it when I open the app. I'll decide when it's time." },
];

function SettingsPanel({ open, onClose, onLogout, onFeedback }) {
  const [rhythm, setRhythm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch('/api/settings').then(r => r.json()).then(d => setRhythm(d.reviewRhythm || 'sunday')).catch(() => setRhythm('sunday'));
  }, [open]);

  if (!open) return null;

  async function pick(value) {
    if (value === rhythm) return;
    const prev = rhythm;
    setRhythm(value); setSaving(true);
    try {
      const r = await fetch('/api/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reviewRhythm: value }) });
      if (!r.ok) throw new Error();
    } catch (_) {
      setRhythm(prev); // revert on failure
    } finally {
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(20,18,15,0.32)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      animation: 'bdFade .15s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 'min(440px, 100%)', maxHeight: '86vh', overflowY: 'auto',
        background: 'var(--stone-bg)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: '1px solid var(--stone-divider)' }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--text)' }}>Settings</span>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-3)', lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ padding: '18px 20px 6px' }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>Review rhythm</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 4 }}>
            When should your weekly reflection become available?
          </div>
        </div>

        <div style={{ paddingBottom: 14 }}>
          {REVIEW_RHYTHMS.map(opt => {
            const sel = rhythm === opt.value;
            return (
              <button key={opt.value} onClick={() => pick(opt.value)} disabled={saving} style={{
                width: '100%', textAlign: 'left', cursor: saving ? 'default' : 'pointer',
                display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 20px',
                background: sel ? 'var(--slate-tint-bg)' : 'transparent', border: 'none',
              }}>
                <span style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                  border: sel ? 'none' : '1.5px solid #C8C4BE', background: sel ? 'var(--slate)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {sel && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                </span>
                <span>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', display: 'block', marginBottom: 3 }}>
                    {opt.label}
                  </span>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: sel ? 'var(--slate)' : 'var(--text-3)', lineHeight: 1.45 }}>
                    {opt.sub}{opt.isDefault && <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}> · Default</span>}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ borderTop: '1px solid var(--stone-divider)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {onFeedback && (
            <button onClick={() => { onClose(); onFeedback(); }} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-3)', fontSize: 13, fontFamily: 'var(--font-body)',
              padding: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Feedback
            </button>
          )}
          {onLogout && (
            <button onClick={onLogout} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-3)', fontSize: 13, fontFamily: 'var(--font-body)',
              padding: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Log out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SunriseBand({ userName, view, onView, onOpenSettings }) {
  return (
    <div className="sunrise-band" style={{ position: 'relative', overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, #2E3F52 0%, #7A6652 40%, #C8B898 72%, #F0EDE8 100%)' }} />
      <GrainOverlay opacity={0.10} />
      {/* One unified header row: greeting left, Briefing/Reflect toggle right. */}
      <div className="hero-row content-col" style={{
        position: 'relative', zIndex: 10, paddingTop: 22, paddingBottom: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 4.6vw, 32px)', fontWeight: 400,
          color: '#fff', lineHeight: 1.1, textShadow: '0 2px 18px rgba(0,0,0,0.16)',
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {getGreeting()}, {userName || 'there'}.
        </div>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <NavToggle view={view} onView={onView} onGradient />
          <GearButton light onClick={onOpenSettings} />
        </div>
      </div>
    </div>
  );
}

// The brief, rendered as the opening assistant message (chat-first; the same first message,
// surfaced at the top of the Briefing). Replies happen in the chat, opened from the capture bar.
function BriefHero({ messages, loading }) {
  const brief = (messages || []).find(m => m.role === 'assistant' && !m.fromUI);
  if (!brief) {
    return <div style={{ padding: '0 0 22px', fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>{loading ? 'Reading your day…' : ''}</div>;
  }
  return (
    <div className="brief-hero" style={{ padding: '0 0 22px', fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 400, color: 'var(--text-primary)', lineHeight: 1.68, whiteSpace: 'pre-wrap' }}>
      {brief.content}
    </div>
  );
}

// Pulse fields: the three manual notes (kept; restyled as quiet labelled lines).
function PulseFields({ pulse }) {
  const rows = [
    ['State of mind', pulse?.ceo_pulse],
    ['Biggest drain', pulse?.biggest_drain],
    ['One projection', pulse?.one_projection],
  ];
  return (
    <div style={{ padding: '24px 24px 16px' }}>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-quaternary)', fontWeight: 500, marginBottom: 20 }}>Pulse</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {rows.map(([label, val]) => (
          <div key={label}>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 8 }}>{label}</div>
            {val
              ? <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--text-primary)', lineHeight: 1.4 }}>{val}</div>
              : <div style={{ height: 1, background: 'var(--stone-divider-light)' }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// Capture bar: the chat composer entry at the bottom of the Briefing. Opens the chat.
function CaptureBar({ onOpen }) {
  return (
    <button onClick={onOpen} className="capture-bar" style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
      background: 'var(--surface)', borderRadius: 24, padding: '11px 18px',
      border: '1px solid var(--stone-border)', cursor: 'pointer', textAlign: 'left',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      <span style={{ fontFamily: 'var(--font-body)', fontSize: 13.5, color: 'var(--text-2)', flex: 1 }}>Capture something…</span>
      <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
        <rect x="4" y="0.5" width="6" height="10" rx="3" fill="#8A847A"/>
        <path d="M1.5 9C1.5 12.1 4 14.5 7 14.5C10 14.5 12.5 12.1 12.5 9" stroke="#8A847A" strokeWidth="1.4" strokeLinecap="round" fill="none"/>
        <line x1="7" y1="14.5" x2="7" y2="17" stroke="#8A847A" strokeWidth="1.4" strokeLinecap="round"/>
        <line x1="4.5" y1="17" x2="9.5" y2="17" stroke="#8A847A" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    </button>
  );
}

// Quiet workstream rows — the mobile Briefing layout (Bold). Tap a row to open its detail.
function WorkstreamRows({ workstreams, onOpen }) {
  if (!workstreams.length) return null;
  return (
    <div style={{ padding: '0 24px' }}>
      {workstreams.map(ws => {
        const open = (ws.tasks || []).filter(t => t.status !== 'completed').length;
        return (
          <button key={ws.id} onClick={() => onOpen(ws.id)} className="ws-row" style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', width: '100%',
            background: 'none', border: 'none', borderBottom: '1px solid var(--stone-divider)',
            cursor: 'pointer', textAlign: 'left',
          }}>
            <span style={{ width: 7, height: 7, background: '#CCCAC4', borderRadius: '50%', flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-primary)', flex: 1 }}>{ws.name}</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-quaternary)' }}>{open} open</span>
            <svg width="6" height="10" viewBox="0 0 6 10" fill="none"><path d="M1 9L5 5L1 1" stroke="#C8C4BE" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        );
      })}
    </div>
  );
}

// Workstream detail — the mobile tap-through (Crimson heading + back nav over the existing
// WorkstreamCard task machinery: quick-add, drag-reorder, complete, delete, undo).
function WorkstreamDetail({ workstream, onBack, onTaskComplete, onTaskDelete, onUndo, onChanged, onReorder, lastCompleted }) {
  const open = (workstream.tasks || []).filter(t => t.status !== 'completed').length;
  return (
    <>
      <div className="detail-nav" style={{ flexShrink: 0, height: 44, display: 'flex', alignItems: 'center', padding: '0 18px', borderBottom: '1px solid var(--stone-border)' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <svg width="10" height="17" viewBox="0 0 10 17" fill="none"><path d="M8.5 1.5L1.5 8.5L8.5 15.5" stroke="#5B7FA6" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#5B7FA6' }}>Briefing</span>
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '24px 24px 8px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, color: 'var(--text-primary)', lineHeight: 1.1, marginBottom: 6 }}>{workstream.name}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-tertiary)' }}>{open} open task{open === 1 ? '' : 's'}</div>
        </div>
        <div style={{ padding: '0 12px 40px' }}>
          <WorkstreamCard workstream={workstream} onTaskComplete={onTaskComplete} onTaskDelete={onTaskDelete} onUndo={onUndo} onChanged={onChanged} onReorder={onReorder} lastCompleted={lastCompleted} />
        </div>
      </div>
    </>
  );
}

function WorkstreamGrid({ workstreams, onTaskComplete, onTaskDelete, onUndo, onChanged, onReorder, lastCompleted }) {
  if (workstreams.length === 0) return (
    <div style={{
      textAlign: 'center', padding: '48px 0',
      color: 'var(--text-3)', fontSize: 13,
    }}>
      No workstreams yet.
    </div>
  );

  return (
    <>
      <CoachMark id="ws">
        Tap the circle to complete a task, the × to remove it, “+ add task” to add one, or drag the grip handle to reorder. For workstreams or anything more involved — just ask in the chat.
      </CoachMark>
      <div className="ws-grid">
      {workstreams.map(ws => (
        <WorkstreamCard
          key={ws.id}
          workstream={ws}
          onTaskComplete={onTaskComplete}
          onTaskDelete={onTaskDelete}
          onUndo={onUndo}
          onChanged={onChanged}
          onReorder={onReorder}
          lastCompleted={lastCompleted}
        />
      ))}
      </div>
    </>
  );
}

function GridModeToggle({ mode, onChange }) {
  const opt = (val, label) => (
    <button
      onClick={() => onChange(val)}
      style={{
        padding: '4px 12px', borderRadius: 7, fontSize: 12, fontWeight: 500,
        border: '1px solid var(--border)',
        background: mode === val ? 'var(--text)' : 'var(--surface)',
        color: mode === val ? 'var(--bg)' : 'var(--text-3)',
        cursor: 'pointer',
      }}
    >{label}</button>
  );
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {opt('workstream', 'By Workstream')}
      {opt('priority', 'By Priority')}
    </div>
  );
}

const BUCKET_LABEL = { awaiting: 'Watch', parked: 'Parked' };
const BUCKET_SECTION_LABEL = { active: 'To-Do', awaiting: 'Watch', parked: 'Parked' };

// Watch / Parked / Priority (Tier 3, 2026-07-22): the flat, cross-workstream "By Priority"
// view. Renders `priorityOrder` (server-computed — see computeFlatPriorityOrder in state.js)
// as one continuous list with labeled section breaks; Watch/Parked rows are visually muted
// (per spec: never mistaken for something actionable today) and skip the complete-checkbox.
const BUCKET_ORDER = ['active', 'awaiting', 'parked'];
const BUCKET_COL_TINT = {
  active: 'var(--surface)',
  awaiting: 'var(--bg)',
  parked: 'var(--bg)',
};

// Watch / Parked / Priority (Tier 3, 2026-07-22) — rebuilt as a 3-column Kanban board per
// cem's explicit direction (a deliberate deviation from the written spec's "flat list with
// labeled sections" framing — see DECISIONS.md). Cross-column drag reclassifies the task's
// bucket (fixes the earlier gap where drag only reordered, never reclassified); in-column
// drag reorders via the same priority_override mechanism as before.
function PriorityBoard({ workstreams, priorityOrder, onTaskComplete, onTaskDelete, onUndo, onChanged, lastCompleted }) {
  const byId = new Map();
  for (const ws of workstreams) {
    for (const t of (ws.tasks || [])) {
      if (t.status === 'completed') continue;
      byId.set(t.id, { ...t, workstreamId: ws.id, workstreamName: ws.name });
    }
  }
  const rows = priorityOrder.map(id => byId.get(id)).filter(Boolean);
  const struck = lastCompleted || null;

  const columns = { active: [], awaiting: [], parked: [] };
  for (const row of rows) {
    (columns[row.bucket] || columns.active).push(row);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function bucketOf(taskId) {
    for (const b of BUCKET_ORDER) if (columns[b].some(r => r.id === taskId)) return b;
    return null;
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;

    const sourceBucket = bucketOf(active.id);
    // Dropped on a column's empty-area droppable (id `col-<bucket>`) or on another task —
    // either way, resolve which bucket the drop landed in.
    const destBucket = over.id.toString().startsWith('col-')
      ? over.id.toString().slice(4)
      : bucketOf(over.id);
    if (!sourceBucket || !destBucket) return;

    if (destBucket !== sourceBucket) {
      // Cross-column drag = bucket reclassification (the fix — this used to be a no-op).
      try {
        await fetch('/api/state/tasks/bucket', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId: active.id, bucket: destBucket }),
        });
      } finally {
        onChanged?.();
      }
      return;
    }

    if (active.id === over.id) return;
    // Same-column drag = priority reorder within that bucket (unchanged from before).
    const ids = columns[sourceBucket].map(r => r.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const newOrder = arrayMove(ids, oldIndex, newIndex);
    const droppedIdx = newOrder.indexOf(active.id);
    const beforeTaskId = droppedIdx > 0 ? newOrder[droppedIdx - 1] : null;
    const afterTaskId = droppedIdx < newOrder.length - 1 ? newOrder[droppedIdx + 1] : null;
    try {
      await fetch('/api/state/tasks/priority-reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: active.id, beforeTaskId, afterTaskId }),
      });
    } finally {
      onChanged?.();
    }
  }

  if (rows.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)', fontSize: 13 }}>
        No open tasks.
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', overflowX: 'auto' }}>
        {BUCKET_ORDER.map(bucket => (
          <PriorityColumn
            key={bucket}
            bucket={bucket}
            rows={columns[bucket]}
            struck={struck}
            onComplete={onTaskComplete}
            onDelete={onTaskDelete}
            onUndo={onUndo}
          />
        ))}
      </div>
    </DndContext>
  );
}

function PriorityColumn({ bucket, rows, struck, onComplete, onDelete, onUndo }) {
  const { setNodeRef } = useDroppable({ id: `col-${bucket}` });
  return (
    <div style={{ flex: '1 1 0', minWidth: 220, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
        color: 'var(--text-4)', padding: '2px 4px 8px',
      }}>{BUCKET_SECTION_LABEL[bucket]} <span style={{ opacity: 0.6 }}>({rows.length})</span></div>
      <div
        ref={setNodeRef}
        style={{
          flex: 1, minHeight: 60,
          background: BUCKET_COL_TINT[bucket], border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden', padding: '4px 0',
        }}
      >
        <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
          {rows.length === 0 ? (
            <div style={{ padding: '12px 13px', fontSize: 12, color: 'var(--text-4)', fontStyle: 'italic' }}>
              Nothing here — drag a task in.
            </div>
          ) : rows.map((row, i) => (
            <PriorityRow
              key={row.id}
              task={row}
              workstreamId={row.workstreamId}
              workstreamName={row.workstreamName}
              completed={struck && struck.id === row.id}
              onComplete={onComplete}
              onDelete={onDelete}
              onUndo={onUndo}
              isLast={i === rows.length - 1}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
}

function PriorityRow({ task, workstreamId, workstreamName, isLast, onComplete, onDelete, onUndo, completed }) {
  const {
    attributes, listeners, setNodeRef, transform, transition,
  } = useSortable({ id: task.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [busy, setBusy] = useState(false);
  const muted = task.bucket === 'awaiting' || task.bucket === 'parked';

  async function handleComplete() {
    if (busy) return;
    setBusy(true);
    try {
      await fetch('/api/state/tasks/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id }),
      });
      onComplete?.({ task, workstreamId });
    } catch (_) {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (busy) return;
    setBusy(true);
    try {
      await fetch('/api/state/tasks/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id }),
      });
      onDelete?.({ task });
    } catch (_) {
      setBusy(false);
    }
  }

  if (completed) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 13px', borderBottom: isLast ? 'none' : '1px solid var(--border-light)' }}>
        <span style={{ width: 14, flexShrink: 0 }} aria-hidden="true" />
        <button onClick={() => onUndo?.(task.id)} title="Undo" style={{
          width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
          border: '1.5px solid #5B7FA6', background: '#5B7FA6', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        }}>
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <span style={{ fontSize: 13, color: 'var(--text-3)', textDecoration: 'line-through', flex: 1 }}>{task.text}</span>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={{
      ...style,
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '6px 13px', borderBottom: isLast ? 'none' : '1px solid var(--border-light)',
      opacity: muted ? 0.65 : 1,
    }}>
      <span {...attributes} {...listeners} style={{ width: 14, flexShrink: 0, cursor: 'grab', touchAction: 'none', color: 'var(--text-4)', marginTop: 2 }}>⠿</span>
      {muted ? (
        <span style={{ width: 16, flexShrink: 0 }} aria-hidden="true" />
      ) : (
        <button onClick={handleComplete} disabled={busy} title="Complete" style={{
          width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
          border: '1.5px solid #C8C4BE', background: 'transparent', cursor: busy ? 'default' : 'pointer', padding: 0,
        }} />
      )}
      <span style={{ fontSize: 13, color: 'var(--text)', flex: 1, lineHeight: 1.4 }}>
        {task.text}
        {task.recurrence_parent_id && (
          <span title="Repeating task" style={{ marginLeft: 6, color: 'var(--text-4)', fontSize: 11 }}>↻</span>
        )}
      </span>
      {task.due_date && (
        <span style={{ fontSize: 10, color: 'var(--text-4)', flexShrink: 0, marginTop: 2 }}>due {task.due_date}</span>
      )}
      {muted && (
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 99,
          background: 'var(--bg)', color: 'var(--text-4)', flexShrink: 0, marginTop: 1,
        }}>{BUCKET_LABEL[task.bucket]}</span>
      )}
      <span style={{ fontSize: 10, color: 'var(--text-4)', flexShrink: 0, marginTop: 2 }}>{workstreamName}</span>
      <button onClick={handleDelete} disabled={busy} title="Delete" style={{
        background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer',
        color: 'var(--text-4)', padding: 0, flexShrink: 0, marginTop: 1,
      }}>×</button>
    </div>
  );
}

function WorkstreamCard({ workstream, onTaskComplete, onTaskDelete, onUndo, onChanged, onReorder, lastCompleted }) {
  const tasks = (workstream.tasks || []).filter(t => t.status !== 'completed');
  const struck = lastCompleted && lastCompleted.workstreamId === workstream.id ? lastCompleted : null;
  const hasRows = tasks.length > 0 || struck;

  // Drag from the grip handle only (distance constraint so taps on the circle/× still
  // register); touch-action:none on the handle keeps page scroll working elsewhere.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = tasks.map(t => t.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder?.(workstream.id, arrayMove(ids, oldIndex, newIndex));
  }

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '11px 13px 9px',
        borderBottom: '1px solid var(--border-light)',
      }}>
        <span style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
          background: '#DDDAD4', color: '#6A6560',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700,
        }}>{workstream.name.charAt(0)}</span>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', flex: 1 }}>
          {workstream.name}
        </span>
        <span style={{
          fontSize: 10, color: 'var(--text-4)',
          background: 'var(--bg)', padding: '1px 6px', borderRadius: 99,
        }}>{tasks.length}</span>
      </div>

      {/* Task rows */}
      <div style={{ padding: '4px 0' }}>
        {!hasRows ? (
          <div style={{
            padding: '8px 13px',
            fontSize: 12, color: 'var(--text-4)', fontStyle: 'italic',
          }}>No open tasks</div>
        ) : (
          <>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                {tasks.map((task, i) => (
                  <SortableTaskRow
                    key={task.id}
                    task={task}
                    workstreamId={workstream.id}
                    isLast={!struck && i === tasks.length - 1}
                    onComplete={onTaskComplete}
                    onDelete={onTaskDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {struck && (
              <TaskRow
                key={struck.id + '_done'}
                task={struck}
                workstreamId={workstream.id}
                isLast
                completed
                onUndo={onUndo}
              />
            )}
          </>
        )}
        <QuickAdd workstreamId={workstream.id} onAdded={onChanged} />
      </div>
    </div>
  );
}

function TaskRow({ task, isLast, workstreamId, onComplete, onDelete, completed = false, onUndo, drag }) {
  // Calm/neutral task circle (Sprint 2): grey ring when open, slate fill when done.
  const ring = '#C8C4BE';
  const checkFill = '#5B7FA6';
  const age = taskAge(task.created_at);
  const [busy, setBusy] = useState(false);
  const [hovered, setHovered] = useState(false);

  async function handleComplete() {
    if (busy) return;
    setBusy(true);
    try {
      await fetch('/api/state/tasks/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id }),
      });
      onComplete?.({ task, workstreamId });
    } catch (_) {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (busy) return;
    setBusy(true);
    try {
      await fetch('/api/state/tasks/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id }),
      });
      onDelete?.({ task });
    } catch (_) {
      setBusy(false);
    }
  }

  // Completed (struck-through) mode: the single latest completion, shown in place
  // until the next completion / undo / refresh. Clicking the filled circle undoes it.
  if (completed) {
    return (
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        padding: '6px 13px',
        borderBottom: isLast ? 'none' : '1px solid var(--border-light)',
      }}>
        {/* spacer to keep the circle aligned with draggable rows' grip handle */}
        <span style={{ width: 14, flexShrink: 0 }} aria-hidden="true" />
        <button
          onClick={() => onUndo?.(task.id)}
          title="Undo — mark active again"
          style={{
            width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
            border: `1.5px solid ${checkFill}`, background: checkFill,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0,
          }}
        >
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <span style={{ fontSize: 12, color: 'var(--text-4)', lineHeight: 1.4, flex: 1, textDecoration: 'line-through' }}>
          {task.text}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={drag?.setNodeRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        padding: '6px 13px',
        borderBottom: isLast ? 'none' : '1px solid var(--border-light)',
        opacity: busy ? 0.4 : (drag?.isDragging ? 0.9 : 1),
        transform: drag ? CSS.Transform.toString(drag.transform) : undefined,
        transition: drag?.transition || 'opacity .2s',
        background: drag?.isDragging ? 'var(--surface)' : undefined,
        position: 'relative',
        zIndex: drag?.isDragging ? 5 : undefined,
        boxShadow: drag?.isDragging ? '0 4px 14px rgba(0,0,0,0.14)' : undefined,
      }}>
      {/* Drag handle — touch-action:none so dragging the grip doesn't scroll the page */}
      {drag && (
        <button
          {...drag.attributes}
          {...drag.listeners}
          title="Drag to reorder"
          aria-label="Drag to reorder"
          style={{
            flexShrink: 0, width: 14, height: 18, padding: 0,
            border: 'none', background: 'none', cursor: 'grab',
            color: 'var(--text-4)', fontSize: 11, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            touchAction: 'none',
            opacity: hovered ? 0.85 : 0.4, transition: 'opacity .15s',
          }}
        >⠿</button>
      )}
      <button
        onClick={handleComplete}
        title="Mark complete"
        style={{
          width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
          border: `1.5px solid ${ring}`,
          background: 'none', cursor: busy ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, transition: 'background .15s',
        }}
      >
        {busy && (
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
            <path d="M1.5 5L4 7.5L8.5 2.5" stroke={checkFill} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>
      <span style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.4, flex: 1 }}>
        {task.text}
        {/* Recurring tasks (Tier 3, 2026-07-22): situational awareness only — no streak
            count, no "4th time", no history. Just "this repeats" + when it's due. */}
        {task.recurrence_parent_id && (
          <span title="Repeating task" style={{ marginLeft: 6, color: 'var(--text-4)', fontSize: 11 }}>↻</span>
        )}
      </span>
      {task.due_date && (
        <span style={{ fontSize: 10, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>
          due {task.due_date}
        </span>
      )}
      {age && !task.due_date && (
        <span style={{ fontSize: 10, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>
          {age}
        </span>
      )}
      {/* Delete (×) — Pulse-neutral remove. Visible on hover/focus; deletes with the
          undo toast as the safety net (no completion recorded). */}
      <button
        onClick={handleDelete}
        title="Delete task"
        aria-label="Delete task"
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        style={{
          width: 16, height: 16, flexShrink: 0,
          border: 'none', background: 'none',
          cursor: busy ? 'default' : 'pointer',
          color: 'var(--text-4)', fontSize: 15, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
          opacity: hovered ? 0.9 : 0,
          transition: 'opacity .15s',
        }}
      >×</button>
    </div>
  );
}

// Sortable wrapper: binds dnd-kit's per-item drag state to a TaskRow. Only active task
// rows are sortable — the struck-through completed row and the quick-add footer are not.
function SortableTaskRow(props) {
  const drag = useSortable({ id: props.task.id });
  return <TaskRow {...props} drag={drag} />;
}

// Quick-add: frictionless, deterministic task entry on each workstream card — no chat,
// no Claude. The task lands in this card's workstream. Type, Enter, done. Esc cancels.
function QuickAdd({ workstreamId, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (adding) inputRef.current?.focus(); }, [adding]);

  async function submit() {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      await fetch('/api/state/tasks/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workstreamId, text: t }),
      });
      setText('');
      setAdding(false);
      onAdded?.();
    } catch (_) {
      /* leave the input open so typed text isn't lost */
    } finally {
      setSaving(false);
    }
  }

  if (!adding) {
    return (
      <button
        onClick={() => setAdding(true)}
        style={{
          display: 'block', width: '100%', textAlign: 'left',
          padding: '7px 13px 7px 35px', marginTop: 2,
          background: 'none', border: 'none',
          borderTop: '1px solid var(--border-light)',
          cursor: 'pointer', color: '#8A847A', fontSize: 12,
          fontFamily: 'var(--font-body)',
        }}
      >+ add task</button>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 13px', marginTop: 2,
      borderTop: '1px solid var(--border-light)',
    }}>
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          else if (e.key === 'Escape') { setText(''); setAdding(false); }
        }}
        onBlur={() => { if (!text.trim()) setAdding(false); }}
        placeholder="New task…"
        maxLength={500}
        style={{
          flex: 1, border: '1px solid var(--border)', borderRadius: 6,
          padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-body)',
          background: 'var(--bg)', color: 'var(--text)', outline: 'none',
        }}
      />
    </div>
  );
}

// ─── Deferred proposals — the dashboard "what is this?" (Stage 4) ────────────
// Stray thoughts parked from the Memories page (a tangent typed into an enrichment
// card's "Something else") surface here, on the dashboard, in the chief-of-staff voice.
// The user files each as a task / memory / workstream, or dismisses. Never auto-filed.
// FLAGGED (cem): the brief said "via the Chief of Staff" — surfaced here as a quiet card
// on the Briefing pane (not a chat message) to avoid touching the chat system prompt.
// ─── Reflect v2 invitation (Sprint 3) ─────────────────────────────────────────
// A calm, dismissible card inside the Briefing inviting the user to reflect when their
// review window is open. Two intensities (calm / prominent) driven by the week's signal
// density. "Not now" dismisses for the week with zero consequence (localStorage flag —
// an explicit, logged exception to the no-localStorage rule, same as the chat panel width).
function ReflectInvitationCard({ onReflect }) {
  const [inv, setInv] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch('/api/reflection/invitation')
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        setInv(d);
        try {
          const key = `bearing360_reflect_dismissed_${d.weekId}`;
          if (window.localStorage.getItem(key) === '1') setDismissed(true);
        } catch (_) {}
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!inv || !inv.show || dismissed) return null;

  function dismiss() {
    try { window.localStorage.setItem(`bearing360_reflect_dismissed_${inv.weekId}`, '1'); } catch (_) {}
    setDismissed(true);
  }

  const prominent = inv.intensity === 'prominent';
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: prominent ? 'var(--stone-bg)' : 'var(--stone-card)',
      border: `1px solid ${prominent ? 'var(--slate-border)' : 'var(--stone-border)'}`,
      borderRadius: 12, padding: '18px 20px', margin: '0 0 16px',
    }}>
      {prominent && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'var(--slate)', borderRadius: '2px 0 0 2px' }} />}
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 500, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: prominent ? 'var(--text-2)' : 'var(--text-disabled)', marginBottom: 10,
      }}>
        Week of {inv.weekRange}
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: prominent ? 20 : 18, color: 'var(--text)', lineHeight: 1.42, marginBottom: prominent ? 8 : 6 }}>
        {prominent ? 'This week meant something. It’s worth a few minutes.' : 'Your week is ready to look at.'}
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 12.5, color: prominent ? '#6A6560' : 'var(--text-2)', lineHeight: 1.6, marginBottom: 16 }}>
        {prominent
          ? 'A decision clarified, a conversation happened, and there is something worth carrying forward.'
          : 'A few things moved, a few remain open, and there may be something worth carrying forward.'}
      </div>
      <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
        <button onClick={onReflect} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--slate)' }}>
          Reflect on the week
        </button>
        <button onClick={dismiss} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-disabled)' }}>
          Not now
        </button>
      </div>
    </div>
  );
}

function ProposalsCards({ workstreams, onResolved }) {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/proposals');
      if (r.ok) { const d = await r.json(); setItems(d.proposals || []); }
    } catch (_) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!items || items.length === 0) return null;

  return (
    <div style={{ marginBottom: 22 }}>
      {items.map(p => (
        <ProposalCard
          key={p.id}
          p={p}
          workstreams={workstreams}
          onDone={(id) => { setItems(list => list.filter(x => x.id !== id)); onResolved?.(); }}
        />
      ))}
    </div>
  );
}

function ProposalCard({ p, workstreams, onDone }) {
  const [stage, setStage] = useState(null); // null | 'task' | 'workstream'
  const [wsId, setWsId] = useState(workstreams?.[0]?.id || '');
  const [wsName, setWsName] = useState(p.text.slice(0, 40));
  const [busy, setBusy] = useState(false);

  async function resolve(resolution, opts = {}) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch('/api/proposals/resolve', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposalId: p.id, resolution, ...opts }),
      });
      if (r.ok) onDone(p.id);
    } catch (_) {} finally { setBusy(false); }
  }

  const pill = (label, onClick, primary) => (
    <button
      onClick={onClick}
      disabled={busy}
      style={{
        border: `1px solid ${primary ? 'var(--accent)' : 'var(--border)'}`,
        background: primary ? 'var(--accent)' : 'none',
        color: primary ? 'var(--bg)' : 'var(--text-2)',
        borderRadius: 99, padding: '4px 13px', fontSize: 12.5,
        fontFamily: 'var(--font-body)', cursor: 'pointer',
      }}
    >{label}</button>
  );

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
      padding: '13px 15px', marginBottom: 10,
    }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginBottom: 5 }}>
        You mentioned this{p.origin ? ` ${p.origin}` : ''}. What is it?
      </div>
      <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, marginBottom: 11, fontFamily: 'var(--font-display)' }}>
        “{p.text}”
      </div>

      {stage === null && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
          {pill('Task', () => setStage('task'))}
          {pill('Memory', () => resolve('memory'))}
          {pill('Workstream', () => setStage('workstream'))}
          <span style={{ flex: 1 }} />
          <button onClick={() => resolve('dismiss')} disabled={busy} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 12, fontFamily: 'var(--font-body)', textDecoration: 'underline', padding: '4px 2px' }}>Dismiss</button>
        </div>
      )}

      {stage === 'task' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Add as a task in</span>
          <select
            value={wsId}
            onChange={e => setWsId(e.target.value)}
            style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '5px 8px', fontSize: 12.5, fontFamily: 'var(--font-body)', background: 'var(--bg)', color: 'var(--text)' }}
          >
            {(workstreams || []).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          {pill('Confirm', () => resolve('task', { workstream: wsId }), true)}
          <button onClick={() => setStage(null)} disabled={busy} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 12, fontFamily: 'var(--font-body)', textDecoration: 'underline', padding: '4px 2px' }}>Back</button>
        </div>
      )}

      {stage === 'workstream' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>New workstream</span>
          <input
            value={wsName}
            onChange={e => setWsName(e.target.value)}
            maxLength={60}
            style={{ flex: 1, minWidth: 120, border: '1px solid var(--border)', borderRadius: 6, padding: '5px 8px', fontSize: 12.5, fontFamily: 'var(--font-body)', background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
          />
          {pill('Confirm', () => resolve('workstream', { name: wsName }), true)}
          <button onClick={() => setStage(null)} disabled={busy} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 12, fontFamily: 'var(--font-body)', textDecoration: 'underline', padding: '4px 2px' }}>Back</button>
        </div>
      )}
    </div>
  );
}

// One question per memory at a time. Saving quietly attaches the attribute (scoped to
// THIS slot only — an answer never spawns or edits another memory). No "skip" anymore
// (Stage 2, cem-approved 2026-06-13): it was ambiguous between "later" and "bad question".
// With no escape hatch the extractor must err toward fewer, surer questions — a marginal
// one now just sits there. "Something else" opens a free-text box for the same slot.
// FLAGGED / GATED (Stage 4): when free text reads as off-topic for the slot (a health
// remark in a finance card), it should be rescued to the deferred-proposals queue rather
// than written here. That queue is a new store (logged decision) — until it exists, free
// text simply fills this slot; off-topic routing lands with Stage 4.
function EnrichmentCard({ memory, onChanged }) {
  const slot = (memory.open_slots || []).find(s => !s.skipped);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [noted, setNoted] = useState(null); // value just saved — brief calm confirmation
  const [deferred, setDeferred] = useState(false); // the answer was a tangent → parked
  const [freeText, setFreeText] = useState(false); // "Something else" expanded

  // Reset whenever the active question changes — one answer must never bleed into the
  // next slot (the bug that double-filled E-Sarj).
  useEffect(() => { setAnswer(''); setFreeText(false); setDeferred(false); }, [slot?.key]);

  if (!slot && !noted) return null;

  const accent = memory.workstream_color || 'var(--text-3)';

  async function save(value) {
    const v = (value || '').trim();
    if (busy || !v) return;
    setBusy(true);
    try {
      const r = await fetch('/api/memories/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memoryId: memory.id, key: slot.key, answer: v }),
      });
      if (!r.ok) return;
      setNoted(v);     // collapse to a quiet confirmation
      setAnswer('');
      onChanged?.();
    } catch (_) {} finally { setBusy(false); }
  }

  // Free-text ("Something else", or a slot with no options) goes through the classifier:
  // an on-topic answer fills the slot; a tangent is parked as a dashboard proposal and the
  // slot stays open. The card reflects which happened.
  async function saveFreeText(value) {
    const v = (value || '').trim();
    if (busy || !v) return;
    setBusy(true);
    try {
      const r = await fetch('/api/memories/enrich-freetext', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memoryId: memory.id, key: slot.key, text: v }),
      });
      if (!r.ok) return;
      const d = await r.json();
      if (d.outcome === 'deferred') setDeferred(true);
      setNoted(v);
      setAnswer('');
      onChanged?.();
    } catch (_) {} finally { setBusy(false); }
  }

  // After answering: a calm "✓ Noted" — no celebration, no flourish. A parked tangent
  // gets a different, equally-quiet line so the user knows where it went.
  if (noted) {
    return (
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-light)', fontSize: 12, color: 'var(--text-3)' }}>
        {deferred ? (
          <>↗ Set aside — <span style={{ color: 'var(--text-2)' }}>{noted}</span> — I'll raise it on your dashboard.</>
        ) : (
          <>✓ Noted — <span style={{ color: 'var(--text-2)' }}>{noted}</span></>
        )}
      </div>
    );
  }

  const opts = Array.isArray(slot.options) && slot.options.length >= 2 ? slot.options : null;

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-light)' }}>
      {/* Lead-in — gentle, optional, de-emphasized */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 7, fontSize: 10.5, color: 'var(--text-4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.6 10.8c.5.4.8 1 .9 1.7h5.4c.1-.7.4-1.3.9-1.7A6 6 0 0 0 12 3z" />
        </svg>
        One thing I'm not sure about
      </div>

      {/* The question */}
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 14.5, color: 'var(--text)', lineHeight: 1.45, marginBottom: 10 }}>
        {slot.question}
      </div>

      {/* Closed-form options + a "Something else" escape into free text; or a plain
          free-text input when the slot has no options. No skip either way. */}
      {opts && !freeText ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
          {opts.map(o => (
            <button
              key={o}
              onClick={() => save(o)}
              disabled={busy}
              style={{
                border: `1px solid ${accent}`, background: 'none', color: 'var(--text)',
                borderRadius: 99, padding: '4px 13px', fontSize: 12.5,
                fontFamily: 'var(--font-body)', cursor: 'pointer',
              }}
            >{o}</button>
          ))}
          <button
            onClick={() => setFreeText(true)}
            disabled={busy}
            style={{
              border: '1px dashed var(--border)', background: 'none', color: 'var(--text-3)',
              borderRadius: 99, padding: '4px 13px', fontSize: 12.5,
              fontFamily: 'var(--font-body)', cursor: 'pointer',
            }}
          >Something else…</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveFreeText(answer); }}
            placeholder={opts ? 'In your own words…' : '…'}
            autoFocus={freeText}
            maxLength={300}
            style={{
              flex: 1, border: '1px solid var(--border)', borderRadius: 6,
              padding: '6px 9px', fontSize: 13, fontFamily: 'var(--font-body)',
              background: 'var(--bg)', color: 'var(--text)', outline: 'none',
            }}
          />
          <button
            onClick={() => saveFreeText(answer)}
            disabled={busy || !answer.trim()}
            style={{
              border: '1px solid var(--border)', borderRadius: 6, background: 'none',
              cursor: 'pointer', color: 'var(--text)', fontSize: 12, padding: '5px 12px',
              fontFamily: 'var(--font-body)', opacity: answer.trim() ? 1 : 0.4,
            }}
          >Save</button>
          {opts && (
            <button
              onClick={() => { setFreeText(false); setAnswer(''); }}
              disabled={busy}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 11.5, fontFamily: 'var(--font-body)', textDecoration: 'underline', padding: '4px 2px' }}
            >Back</button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Memories Pane (the correction utility — grouped, collapsed, settings-style) ──
// The primary memory surface on all widths. Capture is automatic (session close +
// onboarding); this page is read + prune — memories grouped under collapsible arena
// headers (collapsed by default, scannable at a glance), edit corrects in place,
// delete tombstones with the undo toast as the safety net. Deliberately quiet: the
// briefing is the hero, this is the maintenance hatch.

// ─── Synthesis bar (top of the T) ──────────────────────────────────────────
// The SAME situation-read the Briefing shows (one shared brief per user — reused via
// /api/brief, never a second independent summary). Here it sits full-width above the two
// columns and is deliberately *living*: when a memory is written on this page the brief
// is marked stale server-side and re-synthesized in the background; this bar polls for
// the settled read and flashes so the user witnesses the system register what they said.
// FLAGGED (deferred): true phrase→cluster indexing (tap a phrase to scroll its group)
// needs structured segments the prose read no longer carries — the cancelled
// exploded-sentence shape. Not reintroduced here; navigation is via the grouped list.
function SynthesisBar({ writeTick }) {
  const [data, setData] = useState(null);
  const [flash, setFlash] = useState(false);
  const prevGen = useRef(null);
  const timer = useRef(null);
  const flashTimer = useRef(null);

  const load = useCallback(async (watchChange) => {
    try {
      const r = await fetch('/api/brief');
      if (!r.ok) return;
      const d = await r.json();
      setData(d);
      const gen = d.brief?.generatedAt || null;
      if (watchChange && prevGen.current && gen && gen !== prevGen.current) {
        setFlash(true);
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlash(false), 1500);
      }
      prevGen.current = gen;
      // Keep watching while a regeneration is in flight so we catch the settled read.
      if (d.regenerating) {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => load(true), 6000);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    load(false);
    return () => { clearTimeout(timer.current); clearTimeout(flashTimer.current); };
  }, [load]);

  // A memory write on this page marked the brief stale; give the background regen a beat,
  // then poll and flash on the change.
  useEffect(() => {
    if (!writeTick) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(true), 3000);
  }, [writeTick, load]);

  // Keep the bar present even before a read exists, so the page always reads as a T.
  if (!data || (!data.brief && !data.regenerating)) {
    return (
      <div className="synthesis-bar">
        <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>
          Your situation will appear here as memories accumulate.
        </div>
      </div>
    );
  }

  const read = data.brief?.read;

  return (
    <div className={`synthesis-bar${flash ? ' flash' : ''}`}>
      {read ? (
        <>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.6,
            color: 'var(--text-2)', whiteSpace: 'pre-wrap',
          }}>
            {read}
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-4)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Your situation{data.regenerating ? ' · refreshing…' : ''}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>Reading your situation…</div>
      )}
    </div>
  );
}

// ─── Enrichment column (right column, top) ─────────────────────────────────
// The page's "be curious about me" job, moved out of the list rows into its own column.
// Pull, not push: only entities with an open, load-bearing slot show a question. Card
// behaviour is unchanged for Stage 1 (options + free-text + skip). FLAGGED as gated for
// later stages: removing "skip", and a "Something else" free-text option that routes
// off-topic answers to the deferred-proposals queue (needs a logged decision + new store).
function EnrichmentColumn({ memories, onChanged }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 12 }}>
        Curious about
      </div>
      {memories.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-4)', lineHeight: 1.6 }}>
          No open questions right now. When the system is unsure about something in your
          world, a short question shows up here. Answering is always optional.
        </div>
      ) : (
        memories.map(m => (
          <div
            key={m.id}
            style={{
              marginBottom: 14, padding: '11px 12px',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              {m.workstream_color && (
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.workstream_color, flexShrink: 0 }} />
              )}
              <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{m.workstream_name || 'Unfiled'}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4 }}>{m.text}</div>
            <EnrichmentCard memory={m} onChanged={onChanged} />
          </div>
        ))
      )}
    </div>
  );
}

// ─── Capture floor (right column, bottom) ───────────────────────────────────
// The page's native "I just want to tell it something" input, inheriting the removed
// chat rail's job. Capture-only (it does not answer questions). Reconciles a dump against
// existing memories (EDIT/merge known, ADD only new) and shows ONE review gate listing all
// intended writes — each editable, one confirm — then a single consolidated receipt with
// undo. Workstream creation only ever happens via a visible review line.
function CaptureFloor({ onCommitted }) {
  const [mode, setMode] = useState('input'); // input | review | question | receipt
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);    // { writes:[], new_workstreams:[] }
  const [note, setNote] = useState('');      // capture-only guardrail message
  const [clarifyNote, setClarifyNote] = useState(''); // low-confidence workstream disambiguation question
  const [receipt, setReceipt] = useState(null); // { added, edited, workstreams }
  const [undoBundle, setUndoBundle] = useState(null);

  function reset() {
    setMode('input'); setText(''); setPlan(null); setNote(''); setClarifyNote(''); setReceipt(null); setUndoBundle(null);
  }

  async function preview() {
    const t = text.trim();
    if (busy || !t) return;
    setBusy(true);
    try {
      const r = await fetch('/api/memories/capture', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t }),
      });
      if (!r.ok) return;
      const d = await r.json();
      if (d.is_question) { setNote(d.note); setMode('question'); return; }
      const writes = d.writes || [];
      const newWs = d.new_workstreams || [];
      if (writes.length === 0 && newWs.length === 0) {
        setNote("Nothing here looked like standing context to keep — nothing was saved.");
        setMode('question');
        return;
      }
      setClarifyNote(d.note || '');
      setPlan({ writes, new_workstreams: newWs });
      setMode('review');
    } catch (_) {} finally { setBusy(false); }
  }

  function editWrite(i, value) {
    setPlan(p => ({ ...p, writes: p.writes.map((w, idx) => idx === i ? { ...w, text: value } : w) }));
  }
  function removeWrite(i) {
    setPlan(p => ({ ...p, writes: p.writes.filter((_, idx) => idx !== i) }));
  }
  function editWs(i, value) {
    setPlan(p => ({ ...p, new_workstreams: p.new_workstreams.map((n, idx) => idx === i ? value : n) }));
  }
  function removeWs(i) {
    // Also detach any write that referenced this soon-to-be-removed workstream name.
    setPlan(p => {
      const name = p.new_workstreams[i];
      return {
        new_workstreams: p.new_workstreams.filter((_, idx) => idx !== i),
        writes: p.writes.map(w => (w.workstream === name ? { ...w, workstream: null } : w)),
      };
    });
  }

  async function commit() {
    if (busy || !plan) return;
    const writes = plan.writes.filter(w => (w.text || '').trim());
    const new_workstreams = plan.new_workstreams.map(n => n.trim()).filter(Boolean);
    if (writes.length === 0 && new_workstreams.length === 0) { reset(); return; }
    setBusy(true);
    try {
      const r = await fetch('/api/memories/capture/commit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ writes, new_workstreams }),
      });
      if (!r.ok) return;
      const d = await r.json();
      setReceipt(d.receipt); setUndoBundle(d.undo); setMode('receipt');
      onCommitted?.();
    } catch (_) {} finally { setBusy(false); }
  }

  async function undo() {
    if (!undoBundle) return;
    try {
      await fetch('/api/memories/capture/undo', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ undo: undoBundle }),
      });
    } catch (_) {}
    onCommitted?.();
    reset();
  }

  return (
    <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--border-light)' }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
        Tell it something
      </div>

      {mode === 'input' && (
        <>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) preview(); }}
            placeholder="A standing fact, an update, something that changed… (not a question)"
            rows={3}
            maxLength={2000}
            disabled={busy}
            style={{
              width: '100%', border: '1px solid var(--border)', borderRadius: 8,
              padding: '9px 11px', fontSize: 13, fontFamily: 'var(--font-body)',
              background: 'var(--surface)', color: 'var(--text)', outline: 'none',
              resize: 'vertical', lineHeight: 1.5,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <button
              onClick={preview}
              disabled={busy || !text.trim()}
              style={{
                border: '1px solid var(--border)', borderRadius: 7, background: 'var(--accent)',
                color: 'var(--bg)', fontSize: 12.5, fontFamily: 'var(--font-body)',
                padding: '6px 16px', cursor: 'pointer', opacity: (busy || !text.trim()) ? 0.5 : 1,
              }}
            >{busy ? 'Reading…' : 'Capture'}</button>
            <span style={{ fontSize: 11, color: 'var(--text-4)' }}>Reconciles against what's already known.</span>
          </div>
        </>
      )}

      {mode === 'question' && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '11px 13px', background: 'var(--surface)' }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>{note}</div>
          <button onClick={reset} style={capLink}>Back</button>
        </div>
      )}

      {mode === 'review' && plan && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 13px', background: 'var(--surface)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginBottom: 10, lineHeight: 1.5 }}>
            Here's what I'd record. Edit anything, drop what's off, then confirm.
          </div>

          {clarifyNote && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', marginBottom: 10, lineHeight: 1.4 }}>
              {clarifyNote}
            </div>
          )}

          {plan.new_workstreams.map((n, i) => (
            <div key={`ws-${i}`} style={capRow}>
              <span style={capTag}>New workstream</span>
              <input value={n} onChange={e => editWs(i, e.target.value)} maxLength={60} style={capInput} />
              <button onClick={() => removeWs(i)} style={capX} title="Drop">×</button>
            </div>
          ))}

          {plan.writes.map((w, i) => (
            <div key={`w-${i}`} style={{ marginBottom: 9 }}>
              <div style={capRow}>
                <span style={capTag}>{w.op === 'edit' ? 'Updating' : 'Adding'}{w.workstream ? ` · ${w.workstream}` : ''}</span>
                <input value={w.text} onChange={e => editWrite(i, e.target.value)} maxLength={500} style={capInput} />
                <button onClick={() => removeWrite(i)} style={capX} title="Drop">×</button>
              </div>
              {w.op === 'edit' && w.prev_text && (
                <div style={{ fontSize: 11, color: 'var(--text-4)', paddingLeft: 4, marginTop: 2 }}>
                  was: <span style={{ textDecoration: 'line-through' }}>{w.prev_text}</span>
                </div>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <button
              onClick={commit}
              disabled={busy}
              style={{
                border: '1px solid var(--border)', borderRadius: 7, background: 'var(--accent)',
                color: 'var(--bg)', fontSize: 12.5, fontFamily: 'var(--font-body)',
                padding: '6px 16px', cursor: 'pointer', opacity: busy ? 0.5 : 1,
              }}
            >{busy ? 'Saving…' : 'Confirm'}</button>
            <button onClick={reset} style={capLink}>Cancel</button>
          </div>
        </div>
      )}

      {mode === 'receipt' && receipt && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '11px 13px', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-2)', flex: 1 }}>{captureReceiptText(receipt)}</span>
          <button onClick={undo} style={capLink}>Undo</button>
          <button onClick={reset} style={{ ...capLink, color: 'var(--text-2)' }}>Done</button>
        </div>
      )}
    </div>
  );
}

const capRow = { display: 'flex', alignItems: 'flex-start', gap: 7 };
const capTag = { fontSize: 10, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', paddingTop: 7, flexShrink: 0 };
const capInput = { flex: 1, border: '1px solid var(--border)', borderRadius: 6, padding: '5px 8px', fontSize: 12.5, fontFamily: 'var(--font-body)', background: 'var(--bg)', color: 'var(--text)', outline: 'none' };
const capX = { border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 15, lineHeight: 1, padding: '4px 2px', flexShrink: 0 };
const capLink = { border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 12, fontFamily: 'var(--font-body)', textDecoration: 'underline', padding: '4px 2px' };

function captureReceiptText({ added, edited, workstreams }) {
  const parts = [];
  if (added) parts.push(`${added} added`);
  if (edited) parts.push(`${edited} updated`);
  if (workstreams) parts.push(`${workstreams} new workstream${workstreams > 1 ? 's' : ''}`);
  return parts.length ? `Saved · ${parts.join(' · ')}` : 'Saved';
}

function MemoriesPane() {
  const [memories, setMemories] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [undoMem, setUndoMem] = useState(null);
  // First-week gate (mirrors the weekly reflection): memories stay hidden until 7 days
  // after onboarding, then unlock all at once.
  const [gate, setGate] = useState({ gated: false, unlocksAt: null });
  // Arena keys the user has COLLAPSED. Default is empty → every group EXPANDED by default
  // (the correction surface is read-then-fix, not scan-then-open).
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Bumps on every memory write so the synthesis bar re-reads and witnesses the change.
  const [writeTick, setWriteTick] = useState(0);
  // Ids that arrived since the last load — briefly flashed so a memory captured live
  // (e.g. from a task just quick-added) is witnessed, not silently inserted.
  const [flashIds, setFlashIds] = useState(() => new Set());
  const seenIdsRef = useRef(null); // null until the first load completes
  const flashTimerRef = useRef(null);
  const undoTimerRef = useRef(null);

  const toggle = (key) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/memories');
      if (r.ok) {
        const d = await r.json();
        setGate({ gated: !!d.gated, unlocksAt: d.unlocksAt || null });
        const list = d.memories || [];
        // Flash any genuinely new ids (skip the very first load — that's not "new").
        if (seenIdsRef.current) {
          const fresh = list.filter(m => !seenIdsRef.current.has(m.id)).map(m => m.id);
          if (fresh.length) {
            setFlashIds(new Set(fresh));
            clearTimeout(flashTimerRef.current);
            flashTimerRef.current = setTimeout(() => setFlashIds(new Set()), 1600);
          }
        }
        seenIdsRef.current = new Set(list.map(m => m.id));
        setMemories(list);
        setLastUpdated(d.lastUpdated);
      }
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Live catch-up: a task quick-added on another tab triggers a debounced background
  // extraction server-side. Poll briefly after mount so the resulting memory appears
  // here on its own — no page reload. Bounded window (a single Claude call lands in a
  // few seconds); stops itself, never an idle forever-poll.
  useEffect(() => {
    let ticks = 0;
    const iv = setInterval(() => {
      ticks += 1;
      load();
      if (ticks >= 7) clearInterval(iv); // ~21s, then quiet
    }, 3000);
    return () => clearInterval(iv);
  }, [load]);
  useEffect(() => () => { clearTimeout(undoTimerRef.current); clearTimeout(flashTimerRef.current); }, []);

  // Reload the list AND nudge the synthesis bar — every write makes the top of the page
  // re-read so the change is felt, not silently swapped.
  const afterWrite = useCallback(() => { load(); setWriteTick(t => t + 1); }, [load]);

  async function handleDelete(m) {
    try {
      await fetch('/api/memories/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memoryId: m.id }),
      });
      clearTimeout(undoTimerRef.current);
      setUndoMem(m);
      undoTimerRef.current = setTimeout(() => setUndoMem(null), 5000);
      afterWrite();
    } catch (_) {}
  }

  async function handleUndo() {
    if (!undoMem) return;
    clearTimeout(undoTimerRef.current);
    const id = undoMem.id;
    setUndoMem(null);
    try {
      await fetch('/api/memories/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memoryId: id }),
      });
    } catch (_) {}
    afterWrite();
  }

  async function handleEdit(memoryId, text) {
    try {
      await fetch('/api/memories/edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memoryId, text }),
      });
    } catch (_) {}
    afterWrite();
  }

  const updatedLabel = lastUpdated
    ? new Date(lastUpdated).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null;

  // Group by workstream. Heaviest arenas first (that's where correction is most often
  // needed); unfiled memories sort last.
  const groups = (() => {
    const by = new Map();
    for (const m of memories) {
      const key = m.workstream_id || '__unfiled__';
      if (!by.has(key)) by.set(key, { key, name: m.workstream_name || 'Unfiled', color: m.workstream_color || null, items: [] });
      by.get(key).items.push(m);
    }
    return [...by.values()].sort((a, b) =>
      (a.key === '__unfiled__' ? 1 : 0) - (b.key === '__unfiled__' ? 1 : 0)
      || b.items.length - a.items.length
      || a.name.localeCompare(b.name)
    );
  })();

  // Entities with an open, load-bearing slot — the enrichment column's content.
  const enrichable = memories.filter(m => m.kind === 'entity' && (m.open_slots || []).some(s => !s.skipped));

  // Hard gate (first week post-onboarding) — memories build quietly in the background but
  // stay hidden until the unlock date, the same "earn it over a week" rhythm as the Reflect
  // page. Once loaded and gated, the whole tab is the locked panel (no list, no capture).
  if (!loading && gate.gated) {
    const unlockLabel = gate.unlocksAt
      ? new Date(gate.unlocksAt).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
      : 'next week';
    return (
      <div className="memories-tab">
        <div style={{
          maxWidth: 560, margin: '56px auto', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
        }}>
          <div style={{ fontSize: 26, color: 'var(--text-4)' }}>✦</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--text-1)', margin: 0 }}>
            Memories unlock {unlockLabel}
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.65, margin: 0 }}>
            Bearing360 spends your first week quietly learning the standing context behind your
            work — who's who, what's in motion, what you're carrying. Your memories open up on{' '}
            <strong style={{ color: 'var(--text-2)' }}>{unlockLabel}</strong>, the same weekly
            rhythm as your reflection. Nothing to do until then — just keep using it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="memories-tab">
      {/* TOP OF THE T — the living index sentence, full-width. */}
      <SynthesisBar writeTick={writeTick} />

      <div className="memories-t-body">
        {/* LEFT — the correction surface: read it, fix it. Expanded by default. */}
        <div className="memories-left">
          <div style={{ marginBottom: 16, maxWidth: 660 }}>
            <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text-2)', margin: '0 0 2px', letterSpacing: '0.01em' }}>
              Memories
              {memories.length > 0 && (
                <span style={{ fontWeight: 400, color: 'var(--text-4)' }}>
                  {'  '}{memories.length} held{updatedLabel ? ` · updated ${updatedLabel}` : ''}
                </span>
              )}
            </h2>
            <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
              What the system has learned about your world. Edit or forget anything that's off.
            </div>
          </div>

          {loading ? (
            <div style={{ color: 'var(--text-4)', fontSize: 13 }}>Loading…</div>
          ) : memories.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13, lineHeight: 1.6, maxWidth: 480 }}>
              Nothing held yet. Memories are captured quietly from your conversations —
              the standing context behind your tasks will appear here as you talk.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 660 }}>
              {groups.map(g => (
                <MemoryGroup
                  key={g.key}
                  group={g}
                  open={!collapsed.has(g.key)}
                  onToggle={() => toggle(g.key)}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  flashIds={flashIds}
                />
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — enrichment (top) + capture floor (bottom). Never goes fully blank. */}
        <aside className="memories-right">
          <EnrichmentColumn memories={enrichable} onChanged={afterWrite} />
          <CaptureFloor onCommitted={afterWrite} />
        </aside>
      </div>

      {/* Undo toast — fixed to the viewport so it survives either column scrolling. */}
      {undoMem && (
        <div className="fade-in" style={{
          position: 'fixed', bottom: 20, left: 16, width: 'min(440px, calc(100vw - 32px))',
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '10px 14px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 50,
        }}>
          <span style={{ fontSize: 13, color: 'var(--text-2)', flex: 1, fontFamily: 'var(--font-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Forgotten <span style={{ color: 'var(--text-3)' }}>{undoMem.text}</span>
          </span>
          <button
            onClick={handleUndo}
            style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 6,
              cursor: 'pointer', color: 'var(--text)', fontSize: 12,
              fontFamily: 'var(--font-body)', padding: '3px 10px', flexShrink: 0,
            }}
          >Undo</button>
          <button
            onClick={() => { clearTimeout(undoTimerRef.current); setUndoMem(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16, padding: '0 2px', flexShrink: 0 }}
          >×</button>
        </div>
      )}
    </div>
  );
}

// Collapsible arena header + its memories. Collapsed by default; the header alone is
// the scannable index ("Axon  6"). Expanding reveals the rows where edit/delete/
// enrichment live.
function MemoryGroup({ group, open, onToggle, onDelete, onEdit, flashIds }) {
  return (
    <div>
      <button
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 9,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '8px 4px', textAlign: 'left',
          borderBottom: '1px solid var(--border-light)',
        }}
      >
        <span style={{
          fontSize: 10, color: 'var(--text-4)', width: 9, flexShrink: 0,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s',
        }}>▶</span>
        {group.color && (
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: group.color, flexShrink: 0 }} />
        )}
        <span style={{ fontSize: 13, color: 'var(--text)', fontFamily: 'var(--font-body)' }}>{group.name}</span>
        <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{group.items.length}</span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0 12px 18px' }}>
          {group.items.map(m => (
            <MemoryRow key={m.id} memory={m} onDelete={onDelete} onEdit={onEdit} flash={flashIds?.has(m.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function MemoryRow({ memory: m, onDelete, onEdit, flash }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.text);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  function commitEdit() {
    const t = draft.trim();
    setEditing(false);
    if (t && t !== m.text) onEdit(m.id, t);
    else setDraft(m.text);
  }

  const sourceDate = m.created_at
    ? new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null;

  return (
    <div
      className={flash ? 'mem-row-flash' : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '8px 10px',
      }}
    >
      {/* Subtype + delete (the arena is in the group header, so no workstream chip here) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {m.subtype && (
          <span style={{ fontSize: 10, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.subtype}</span>
        )}
        <span style={{ flex: 1 }} />
        <button
          onClick={() => onDelete(m)}
          title="Forget this memory"
          aria-label="Forget this memory"
          style={{
            width: 15, height: 15, flexShrink: 0,
            border: 'none', background: 'none', cursor: 'pointer',
            color: 'var(--text-4)', fontSize: 14, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
            opacity: hovered ? 0.9 : 0, transition: 'opacity .15s',
          }}
        >×</button>
      </div>

      {/* Memory text — click to correct in place */}
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit();
            else if (e.key === 'Escape') { setDraft(m.text); setEditing(false); }
          }}
          onBlur={() => { setDraft(m.text); setEditing(false); }}
          maxLength={500}
          style={{
            width: '100%', border: '1px solid var(--border)', borderRadius: 6,
            padding: '4px 8px', fontSize: 13, fontFamily: 'var(--font-body)',
            background: 'var(--bg)', color: 'var(--text)', outline: 'none',
          }}
        />
      ) : (
        <div
          onClick={() => setEditing(true)}
          title="Click to correct"
          style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4, cursor: 'text' }}
        >
          {m.text}
        </div>
      )}

      {/* Source quote — the trust mechanic. Hidden at rest to keep the list scannable;
          revealed on hover (same gesture as the delete ×). */}
      {hovered && m.source_quote && (
        <div style={{
          marginTop: 5, paddingTop: 5, borderTop: '1px solid var(--border-light)',
          fontSize: 11.5, color: 'var(--text-3)',
        }}>
          {sourceDate && (
            <span style={{ fontSize: 10, color: 'var(--text-4)', display: 'block', marginBottom: 1 }}>
              From your session · {sourceDate}
            </span>
          )}
          <span style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic' }}>
            “{m.source_quote}”
          </span>
        </div>
      )}

      {/* Enrichment moved out of the row into the right-column EnrichmentColumn
          (the page's "be curious about me" surface). The row is now purely correction. */}
    </div>
  );
}

// ─── Reflection Pane ──────────────────────────────────────────────────────────

// ─── Reflect v2 (Sprint 3): the AI reads the week ──────────────────────────────
// Energy dot for the weekly strip, artboard states: H solid slate, M slate-65%,
// L stone, not-logged dashed ring, plus a today ring.
function reflectDot(state) {
  if (state === 'H') return { bg: 'var(--slate)', label: 'H', color: '#fff', weight: 600 };
  if (state === 'M') return { bg: 'rgba(91,127,166,0.65)', label: 'M', color: '#fff', weight: 600 };
  if (state === 'L') return { bg: 'var(--stone-divider-light)', label: 'L', color: '#B0ACA7', weight: 500 };
  return null; // not logged
}

const REFLECT_EYEBROW = {
  fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 500,
  letterSpacing: '0.14em', textTransform: 'uppercase', color: '#B0ACA7',
};

function ReflectionPane() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  // Closing handoff ("Looking ahead"). Optional + skippable; empty is valid.
  const [ahead, setAhead] = useState('');
  const [aheadSaving, setAheadSaving] = useState(false);
  const [lookingDone, setLookingDone] = useState(false);

  // Generating state: a calm breathing phrase that rotates slowly (no spinner, no %).
  const PHRASES = [
    "Reading the week's conversations, tasks, and energy check-ins...",
    'Looking at what moved, and what stayed still...',
    'Finding the thread of the week...',
  ];
  const [phrase, setPhrase] = useState(0);
  const busy = loading || regenerating;
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setPhrase(p => (p + 1) % PHRASES.length), 4000);
    return () => clearInterval(t);
  }, [busy]); // eslint-disable-line

  function applyData(d) {
    setData(d);
    setAhead(d.lookingAhead || '');
    setLookingDone(false);
  }

  useEffect(() => {
    fetch('/api/reflection')
      .then(r => r.json())
      .then(d => { applyData(d); setLoading(false); })
      .catch(() => { setError('Failed to load reflection'); setLoading(false); });
  }, []);

  async function regenerate() {
    setRegenerating(true); setError(null);
    try {
      const r = await fetch('/api/reflection/generate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data?.weekStart ? { weekStart: data.weekStart } : {}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      applyData(d);
    } catch (e) {
      setError(e.message || 'Generation failed');
    } finally {
      setRegenerating(false);
    }
  }

  async function saveAhead() {
    setAheadSaving(true);
    try {
      await fetch('/api/reflection/looking-ahead', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: ahead }),
      });
      setLookingDone(true);
    } catch (_) {
    } finally {
      setAheadSaving(false);
    }
  }

  const wrap = { flex: 1, overflowY: 'auto', padding: '28px 28px 64px' };
  const col = { maxWidth: 640, margin: '0 auto' };
  const divider = { height: 1, background: 'var(--stone-divider-light)', margin: '22px 0' };

  // ── Generating / loading state (artboard 04) — stays inside the Reflect shell ──
  if (busy) {
    const skel = (h, w, bg, delay) => (
      <div style={{ height: h, width: w, borderRadius: h / 2, background: bg,
        animation: `shimmer 2.2s ease-in-out ${delay}s infinite` }} />
    );
    return (
      <div style={wrap}>
        <div style={col}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 400, color: 'var(--text)', margin: '0 0 4px' }}>
            {data?.weekRange ? `Week of ${data.weekRange}` : 'Reading your week'}
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-3)', margin: '0 0 40px' }}>
            {regenerating ? 'Re-reading the week' : 'Just a moment'}
          </p>
          <p style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 19, color: 'var(--text-2)',
            lineHeight: 1.65, margin: '0 0 34px', animation: 'breathe 3.5s ease-in-out infinite' }}>
            {PHRASES[phrase]}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 26 }}>
            {skel(10, '94%', 'var(--stone-divider-light)', 0)}
            {skel(10, '82%', 'var(--stone-divider-light)', 0.3)}
            {skel(10, '68%', 'var(--stone-divider-light)', 0.6)}
          </div>
          <div style={{ height: 1, background: 'var(--stone-divider)', marginBottom: 24 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {skel(8, '48%', '#ECEAE6', 0.2)}
            {skel(8, '70%', '#ECEAE6', 0.5)}
            {skel(8, '58%', '#ECEAE6', 0.8)}
          </div>
          <div style={{ display: 'flex', gap: 7, marginTop: 40 }}>
            {[0, 0.22, 0.44].map((d, i) => (
              <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--slate)',
                animation: `dpulse 1.4s ease-in-out ${d}s infinite` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Hard error ──
  if (error && !data?.reflection) {
    return (
      <div style={wrap}><div style={col}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7 }}>
          Couldn't read the week right now.{' '}
          <button onClick={regenerate} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--slate)', textDecoration: 'underline' }}>
            Try again
          </button>.
        </p>
      </div></div>
    );
  }

  // ── Nothing to reflect on yet (calm empty state, not a gate) ──
  if (!data?.reflection) {
    return (
      <div style={wrap}><div style={col}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 400, color: 'var(--text)', margin: '0 0 10px' }}>
          Nothing to look back on yet
        </h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7 }}>
          Once you've spent a week with Bearing360, your reflection will appear here, a quiet read of how it actually went.
        </p>
      </div></div>
    );
  }

  // ── The reflection (artboards 02 / 03) ──
  const r = data.reflection;
  const thin = !r.whatHadMomentum || r.whatHadMomentum.length < 80;
  const narrativeColor = thin ? '#4A4845' : 'var(--text-primary)';
  const strip = (data.energy && data.energy.strip) || [];
  const logged = strip.filter(d => d.state).length;
  const todayStr = new Date().toLocaleDateString('en-CA');
  const generatedAt = data.generatedAt
    ? new Date(data.generatedAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
    : null;

  return (
    <div style={wrap}>
      <div style={col} className="fade-in">

        {/* Week header */}
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 400, color: 'var(--text)', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
          {data.weekRange ? `Week of ${data.weekRange}` : 'This week'}
        </h2>
        {generatedAt && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-3)', margin: 0 }}>
            {data.isThisWeek === false ? 'From ' : ''}{generatedAt}
          </p>
        )}
        <div style={divider} />

        {/* What had momentum — unlabeled headline, the opening read */}
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 19, color: narrativeColor, lineHeight: 1.72, margin: 0 }}>
          {r.whatHadMomentum}
        </p>
        <div style={divider} />

        {/* What changed shape */}
        {r.whatChangedShape && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ ...REFLECT_EYEBROW, marginBottom: 10 }}>What changed shape</div>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: 'var(--text)', lineHeight: 1.68, margin: 0 }}>
              {r.whatChangedShape}
            </p>
          </div>
        )}

        {/* What created friction */}
        {r.whatCreatedFriction && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ ...REFLECT_EYEBROW, marginBottom: 10 }}>What created friction</div>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: 'var(--text)', lineHeight: 1.68, margin: 0 }}>
              {r.whatCreatedFriction}
            </p>
          </div>
        )}

        {/* Carrying forward */}
        <div style={{ marginBottom: 4 }}>
          <div style={{ ...REFLECT_EYEBROW, marginBottom: 10 }}>Carrying forward</div>
          <p style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: 17, color: 'var(--text)', lineHeight: 1.68, margin: 0 }}>
            {r.whatsCarryingForward}
          </p>
        </div>

        {/* Energy strip */}
        {strip.length === 7 && (
          <>
            <div style={divider} />
            <div style={{ ...REFLECT_EYEBROW, marginBottom: 13 }}>Energy{data.weekRange ? ` · ${data.weekRange}` : ''}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 360 }}>
              {strip.map(day => {
                const d = reflectDot(day.state);
                const isToday = data.isThisWeek !== false && day.date === todayStr;
                const wd = new Date(day.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
                return (
                  <div key={day.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: d ? d.bg : 'transparent',
                      border: d ? 'none' : '1.5px dashed #C8C4BE',
                      boxShadow: isToday ? '0 0 0 2px var(--bg), 0 0 0 3.5px var(--slate)' : 'none',
                    }}>
                      {d && <span style={{ fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: d.weight, color: d.color }}>{d.label}</span>}
                    </span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 9, color: isToday ? 'var(--slate)' : 'var(--text-4)', fontWeight: isToday ? 500 : 400 }}>{wd[0]}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontFamily: 'var(--font-body)', fontSize: 10.5, color: 'var(--text-3)' }}>
              {logged} of 7 logged
            </div>
          </>
        )}

        {/* Looking ahead — optional closing handoff */}
        <div style={divider} />
        <div style={{ ...REFLECT_EYEBROW, marginBottom: 12 }}>Looking ahead</div>
        {lookingDone ? (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)', lineHeight: 1.6, margin: 0 }}>
            {ahead.trim() ? 'Saved for next week.' : 'Skipped, nothing lost.'}{' '}
            <button onClick={() => setLookingDone(false)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--slate)' }}>Edit</button>
          </p>
        ) : (
          <>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--text)', margin: '0 0 12px', lineHeight: 1.35 }}>
              What would make next week good?
            </p>
            <textarea
              value={ahead}
              onChange={e => { setAhead(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px'; }}
              placeholder="Optional, a focus, a result, a feeling."
              rows={1}
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'none', outline: 'none',
                background: 'transparent', border: 'none', borderBottom: '1px solid var(--stone-divider-light)',
                padding: '0 0 10px', minHeight: 30, overflow: 'hidden',
                fontFamily: 'var(--font-display)', fontSize: 16,
                fontStyle: ahead ? 'normal' : 'italic',
                color: ahead ? 'var(--text)' : 'var(--text-disabled)', lineHeight: 1.6,
              }}
            />
            <div style={{ display: 'flex', gap: 20, marginTop: 14, alignItems: 'center' }}>
              <button onClick={() => setLookingDone(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-disabled)' }}>
                Skip
              </button>
              <button onClick={saveAhead} disabled={aheadSaving} style={{ background: 'none', border: 'none', padding: 0, cursor: aheadSaving ? 'default' : 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--slate)' }}>
                {aheadSaving ? 'Saving…' : 'Save and close'}
              </button>
            </div>
          </>
        )}

        {/* Quiet regenerate affordance */}
        <div style={{ marginTop: 28 }}>
          <button onClick={regenerate} disabled={regenerating} style={{
            padding: '6px 14px', borderRadius: 7, background: 'none', border: '1px solid var(--border)',
            color: 'var(--text-3)', cursor: regenerating ? 'default' : 'pointer',
            fontFamily: 'var(--font-body)', fontSize: 11,
          }}>
            {regenerating ? 'Re-reading…' : 'Re-read the week'}
          </button>
        </div>

      </div>
    </div>
  );
}

// ─── Chat Pane ────────────────────────────────────────────────────────────────

function ChatPane({
  messages, setMessages,
  sessionId, setSessionId,
  pendingAction, setPendingAction,
  anxietyCardOpen, setAnxietyCardOpen, anxietyPrefill,
  onStateChange, mobileVisible, onClose, desktop, railWidth,
}) {
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [sessionReady, setSessionReady] = useState(!!sessionId);
  const [opening, setOpening] = useState(false);
  const [newSessionConfirming, setNewSessionConfirming] = useState(false);
  const [newSessionClosing, setNewSessionClosing] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // Deterministic one-line description of the pending destructive action (from the server),
  // shown above the Confirm/Cancel buttons so the user always knows what they're confirming.
  const [confirmSummary, setConfirmSummary] = useState(null);
  // Confirmation checkpoint (decided 2026-07-22): a dense multi-fact turn (3+ CLAIMS) queues
  // its writes for review instead of executing immediately. Each item carries its own
  // checked state so the user can drop individual lines before confirming the batch.
  const [checkpointItems, setCheckpointItems] = useState(null);
  // Bundled questions (2026-07-22): folded into the same checkpoint — 2+ distinct QUESTIONS
  // in one turn render as individually-answerable rows instead of one paragraph.
  const [checkpointQuestions, setCheckpointQuestions] = useState(null);
  const [checkpointLoading, setCheckpointLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const initdRef = useRef(false);
  const sendFnRef = useRef(null);

  const [handoverLoading, setHandoverLoading] = useState(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading, opening, anxietyCardOpen]);

  // "Hand this over": pool the card's fields into one blob, assess + commit in one call,
  // drop the Chief-of-Staff reply into the thread. No review-gate (per the brief).
  async function submitHandover(pooledText) {
    if (!pooledText.trim() || handoverLoading) return;
    setHandoverLoading(true);
    try {
      const r = await fetch('/api/memories/handover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: pooledText }),
      });
      const data = await r.json();
      if (data.success) {
        setAnxietyCardOpen(false);
        const reply = (data.reply || '').trim() || "Got it — I've sorted that and put it where it belongs.";
        setMessages(m => [...m, { role: 'assistant', content: reply }]);
        onStateChange();
      } else {
        setMessages(m => [...m, { role: 'assistant', content: data.error || 'Could not sort that just now — try again.', fromUI: true }]);
      }
    } catch (e) {
      console.error('Handover error', e);
      setMessages(m => [...m, { role: 'assistant', content: 'Connection error. Please try again.', fromUI: true }]);
    } finally {
      setHandoverLoading(false);
    }
  }

  useEffect(() => {
    if (initdRef.current) return;
    initdRef.current = true;
    const hasRestored = messages.length > 0;

    (async () => {
      try {
        if (!sessionId) {
          const r = await fetch('/api/session/open', { method: 'POST' });
          const data = await r.json();
          setSessionId(data.sessionId);
        }
        setSessionReady(true);

        if (hasRestored) return;

        setOpening(true);
        const openR = await fetch('/api/chat/open', { method: 'POST' });
        const openData = await openR.json();
        if (openData.response) {
          setMessages([{ role: 'assistant', content: openData.response }]);
        }
      } catch (e) {
        console.error('Session init failed', e);
      } finally {
        setOpening(false);
      }
    })();
  }, []); // eslint-disable-line

  async function send(text) {
    if (!text.trim() || chatLoading) return;

    const userMsg = { role: 'user', content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setChatLoading(true);
    setPendingAction(null);
    setConfirmSummary(null);
    setCheckpointItems(null);
    setCheckpointQuestions(null);
    setValidationError(null);

    try {
      const r = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          conversationHistory: messages.filter(m => !m.fromUI),
          actionId: uuidv4(),
        }),
      });
      const data = await r.json();

      if (data.error) {
        setMessages([...next, { role: 'assistant', content: 'Something went wrong. Please try again.', fromUI: true }]);
        return;
      }

      let responseText = data.response;
      if (data.historyResult) responseText += '\n\n' + data.historyResult;

      setMessages([...next, { role: 'assistant', content: responseText }]);
      if (data.confirmRequired) { setPendingAction(data.confirmRequired); setConfirmSummary(data.confirmSummary || null); }
      if (data.checkpointRequired) {
        const items = data.checkpointRequired.items || [];
        const qs = data.checkpointRequired.questions || [];
        setCheckpointItems(items.length ? items.map(it => ({ ...it, checked: true })) : null);
        setCheckpointQuestions(qs.length ? qs.map(q => ({ question: q, answer: '' })) : null);
      }
      if (data.validationError) setValidationError(data.validationError);
      if (data.stateChanged) onStateChange();
    } catch {
      setMessages([...next, { role: 'assistant', content: 'Connection error. Please try again.', fromUI: true }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  sendFnRef.current = send;

  async function confirmAction() {
    if (!pendingAction || confirmLoading) return;
    setConfirmLoading(true);
    try {
      const r = await fetch('/api/chat/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingAction, actionId: uuidv4() }),
      });
      const data = await r.json();
      if (data.success) {
        setPendingAction(data.confirmRequired || null);
        setConfirmSummary(data.confirmRequired ? (data.confirmSummary || null) : null);
        onStateChange();
        const msg = data.response
          ? { role: 'assistant', content: data.response }
          : { role: 'assistant', content: 'Done.', fromUI: true };
        setMessages(m => [...m, msg]);
      } else {
        setMessages(m => [...m, { role: 'assistant', content: 'Something went wrong confirming that action.', fromUI: true }]);
      }
    } catch (e) {
      console.error(e);
      setMessages(m => [...m, { role: 'assistant', content: 'Something went wrong confirming that action.', fromUI: true }]);
    } finally {
      setConfirmLoading(false);
    }
  }

  function toggleCheckpointItem(idx) {
    setCheckpointItems(items => items.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it));
  }

  function answerCheckpointQuestion(idx, answer) {
    setCheckpointQuestions(qs => qs.map((q, i) => i === idx ? { ...q, answer } : q));
  }

  // Yes/No is a complete answer on its own — when it's the only question in the checkpoint
  // (no write items alongside it), clicking Yes/No submits immediately instead of requiring
  // a separate Confirm click. Bundled multi-question or item+question turns still need the
  // explicit Confirm step since there's more than one thing to review before submitting.
  function quickAnswerCheckpointQuestion(idx, answer) {
    const updated = checkpointQuestions.map((q, i) => i === idx ? { ...q, answer } : q);
    setCheckpointQuestions(updated);
    if (!checkpointItems && updated.length === 1) {
      confirmCheckpoint(updated);
    }
  }

  function cancelCheckpoint() {
    setCheckpointItems(null);
    setCheckpointQuestions(null);
    setMessages(m => [...m, { role: 'assistant', content: "Okay — skipping those for now.", fromUI: true }]);
  }

  // Combined confirm: executes checked write-items (via the checkpoint endpoint) AND sends
  // answered questions as one composed follow-up chat message — both may be present on the
  // same turn (2026-07-22, folding bundled questions into the same checkpoint).
  async function confirmCheckpoint(overrideQuestions) {
    if (checkpointLoading) return;
    const toWrite = (checkpointItems || []).filter(it => it.checked);
    const answered = (overrideQuestions || checkpointQuestions || []).filter(q => q.answer.trim());

    if (toWrite.length === 0 && answered.length === 0) { cancelCheckpoint(); return; }

    setCheckpointLoading(true);
    try {
      if (toWrite.length > 0) {
        const r = await fetch('/api/chat/checkpoint/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toWrite.map(it => ({ action: it.action })), actionId: uuidv4() }),
        });
        const data = await r.json();
        if (data.success) {
          onStateChange();
          const msg = data.response
            ? { role: 'assistant', content: data.response }
            : { role: 'assistant', content: 'Done.', fromUI: true };
          setMessages(m => [...m, msg]);
        } else {
          setMessages(m => [...m, { role: 'assistant', content: 'Something went wrong writing those.', fromUI: true }]);
        }
      }

      setCheckpointItems(null);
      setCheckpointQuestions(null);

      if (answered.length > 0) {
        const composed = answered.map(q => `${q.question} ${q.answer.trim()}`).join('\n');
        setCheckpointLoading(false);
        await sendFnRef.current?.(composed);
        return;
      }
    } catch (e) {
      console.error(e);
      setCheckpointItems(null);
      setCheckpointQuestions(null);
      setMessages(m => [...m, { role: 'assistant', content: 'Something went wrong with that.', fromUI: true }]);
    } finally {
      setCheckpointLoading(false);
    }
  }

  async function handleNewSession() {
    setNewSessionClosing(true);
    try {
      setMessages([]);
      setPendingAction(null);
      setConfirmSummary(null);
      setCheckpointItems(null);
      setCheckpointQuestions(null);
      setValidationError(null);
      setNewSessionConfirming(false);
      setOpening(true);
      const r = await fetch('/api/session/open', { method: 'POST' });
      const data = await r.json();
      setSessionId(data.sessionId);
      const openR = await fetch('/api/chat/open', { method: 'POST' });
      const openData = await openR.json();
      if (openData.response) {
        setMessages([{ role: 'assistant', content: openData.response }]);
      }
    } catch (e) {
      console.error('New session error', e);
    } finally {
      setNewSessionClosing(false);
      setOpening(false);
    }
  }

  function handleInputChange(e) {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  }

  return (
    <div className={`pane-chat${desktop ? ' pane-chat--rail' : (mobileVisible ? ' mobile-visible' : '')}`} style={desktop ? { width: railWidth } : undefined}>
      {/* Header — title; grab handle + close are bottom-sheet (mobile) affordances. */}
      <div style={{ flexShrink: 0, borderBottom: '1px solid var(--border)' }}>
        {!desktop && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--stone-border)' }} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', padding: desktop ? '14px 20px 12px' : '8px 14px 12px 20px' }}>
          <div style={{
            fontSize: 11, fontWeight: 600, flex: 1,
            color: 'var(--text-3)', letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>Chief of Staff</div>
          {!desktop && onClose && (
            <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4, display: 'flex', lineHeight: 1 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: 16,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {opening && (
          <div style={{
            textAlign: 'center', color: 'var(--text-3)',
            fontSize: 13, fontStyle: 'italic', padding: '40px 0',
          }}>
            <div style={{ fontSize: 18, marginBottom: 8 }}>✦</div>
            Opening session…
          </div>
        )}

        <CoachMark id="chat">
          This is your command line. Ask me to add a workstream, complete a task, or just start talking through your week.
        </CoachMark>

        {!opening && messages.length === 0 && !chatLoading && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', padding: '40px 24px', gap: 10,
          }}>
            <div style={{ fontSize: 20, color: 'var(--text-4)' }}>✦</div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-3)', margin: 0, lineHeight: 1.6 }}>
              Want to add a workstream or task?<br />
              Just tell me here — I'll take care of it.
            </p>
          </div>
        )}
        {(() => {
          const openerIdx = messages.findIndex(m => m.role === 'assistant' && !m.fromUI);
          return messages.map((m, i) => <MessageBubble key={i} message={m} opener={i === openerIdx} />);
        })()}
        {chatLoading && <TypingIndicator />}

        {anxietyCardOpen && (
          <AnxietyCaptureCard
            prefill={anxietyPrefill}
            loading={handoverLoading}
            onSubmit={submitHandover}
            onClose={() => setAnxietyCardOpen(false)}
          />
        )}

        {(checkpointItems || checkpointQuestions) && !chatLoading && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4,
            padding: '10px 12px', borderRadius: 10,
            background: 'var(--bg)', border: '1px solid var(--border)',
          }}>
            {checkpointItems && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {checkpointItems.map((item, i) => (
                  <label key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.45, cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => toggleCheckpointItem(i)}
                      style={{ marginTop: 2, flexShrink: 0 }}
                    />
                    <span>{item.description || item.action?.type}</span>
                  </label>
                ))}
              </div>
            )}
            {/* Bundled questions (2026-07-22): each distinct QUESTIONS entry gets its own
                answerable row — quick Yes/No fills the field, or type a short free-text reply. */}
            {checkpointQuestions && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {checkpointQuestions.map((q, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.45 }}>{q.question}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => quickAnswerCheckpointQuestion(i, 'Yes.')}
                        style={{
                          padding: '4px 10px', borderRadius: 6, fontSize: 12,
                          border: '1px solid var(--border)',
                          background: q.answer === 'Yes.' ? 'var(--text)' : 'var(--surface)',
                          color: q.answer === 'Yes.' ? 'var(--bg)' : 'var(--text-2)',
                          cursor: 'pointer',
                        }}
                      >Yes</button>
                      <button
                        onClick={() => quickAnswerCheckpointQuestion(i, 'No.')}
                        style={{
                          padding: '4px 10px', borderRadius: 6, fontSize: 12,
                          border: '1px solid var(--border)',
                          background: q.answer === 'No.' ? 'var(--text)' : 'var(--surface)',
                          color: q.answer === 'No.' ? 'var(--bg)' : 'var(--text-2)',
                          cursor: 'pointer',
                        }}
                      >No</button>
                      <input
                        type="text"
                        value={q.answer === 'Yes.' || q.answer === 'No.' ? '' : q.answer}
                        onChange={(e) => answerCheckpointQuestion(i, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          if (checkpointItems || checkpointQuestions.length !== 1) return;
                          if (!e.target.value.trim()) return;
                          e.preventDefault();
                          confirmCheckpoint();
                        }}
                        placeholder="or type a short reply…"
                        style={{
                          flex: 1, fontSize: 12, padding: '4px 8px', borderRadius: 6,
                          border: '1px solid var(--border)', background: 'var(--surface)',
                          color: 'var(--text)',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              {/* A lone question (no write items alongside it) submits itself the moment
                  Yes/No/Enter is used — see quickAnswerCheckpointQuestion. The explicit
                  Confirm step only earns its keep when there's a batch to review first:
                  checked write items, or 2+ bundled questions to answer before submitting. */}
              {!(!checkpointItems && checkpointQuestions && checkpointQuestions.length === 1) && (
                <button
                  onClick={() => confirmCheckpoint()}
                  disabled={checkpointLoading}
                  style={{
                    flex: 1, padding: '9px', borderRadius: 8,
                    background: checkpointLoading ? 'var(--border)' : 'var(--confirm)',
                    color: checkpointLoading ? 'var(--text-3)' : '#fff',
                    fontSize: 13, fontWeight: 500,
                    cursor: checkpointLoading ? 'default' : 'pointer',
                  }}
                >{checkpointLoading ? 'Working…' : 'Confirm'}</button>
              )}
              <button
                onClick={cancelCheckpoint}
                disabled={checkpointLoading}
                style={{
                  flex: 1, padding: '9px', borderRadius: 8,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  fontSize: 13,
                  opacity: checkpointLoading ? 0.5 : 1,
                  cursor: checkpointLoading ? 'default' : 'pointer',
                }}
              >Cancel</button>
            </div>
          </div>
        )}

        {pendingAction && !chatLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {confirmSummary && (
              <div style={{
                fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.45,
                padding: '8px 12px', borderRadius: 8,
                background: 'var(--bg)', border: '1px solid var(--border)',
              }}>
                <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>Confirm: </span>{confirmSummary}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={confirmAction}
              disabled={confirmLoading}
              style={{
                flex: 1, padding: '9px', borderRadius: 8,
                background: confirmLoading ? 'var(--border)' : 'var(--confirm)',
                color: confirmLoading ? 'var(--text-3)' : '#fff',
                fontSize: 13, fontWeight: 500,
                cursor: confirmLoading ? 'default' : 'pointer',
              }}
            >{confirmLoading ? 'Confirming…' : 'Confirm'}</button>
            <button
              onClick={() => { setPendingAction(null); setConfirmSummary(null); }}
              disabled={confirmLoading}
              style={{
                flex: 1, padding: '9px', borderRadius: 8,
                background: 'var(--bg)', border: '1px solid var(--border)',
                fontSize: 13,
                opacity: confirmLoading ? 0.5 : 1,
                cursor: confirmLoading ? 'default' : 'pointer',
              }}
            >Cancel</button>
            </div>
          </div>
        )}

        {validationError && (
          <div className="chat-error" style={{
            fontSize: 12, color: 'var(--danger)',
            padding: '6px 10px', borderRadius: 8,
          }}>
            ⚠ Action could not be processed. Claude has been notified.
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 16px 16px',
        borderTop: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        {newSessionConfirming && (
          <div style={{
            marginBottom: 8,
            padding: '10px 14px',
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: 10,
          }}>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 8 }}>
              Start a new session? Today's conversation will still appear in history at end of day.
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setNewSessionConfirming(false)}
                disabled={newSessionClosing}
                style={{
                  fontSize: 12, padding: '4px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--surface)',
                  color: 'var(--text-2)', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={handleNewSession}
                disabled={newSessionClosing}
                style={{
                  fontSize: 12, padding: '4px 12px', borderRadius: 6,
                  border: 'none', background: 'var(--text)',
                  color: 'var(--bg)',
                  cursor: newSessionClosing ? 'default' : 'pointer',
                  opacity: newSessionClosing ? 0.6 : 1,
                }}
              >{newSessionClosing ? 'Saving…' : 'Start new'}</button>
            </div>
          </div>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9,
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 14, padding: '10px 12px 10px 14px',
          transition: 'border-color .15s',
        }}>
          <span style={{ color: 'var(--text-4)', fontSize: 13, flexShrink: 0 }}>✦</span>
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKey}
            placeholder="What's on your mind?"
            rows={1}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              resize: 'none', fontFamily: 'var(--font-body)',
              fontSize: 13, color: 'var(--text)', lineHeight: 1.5,
              height: 'auto', minHeight: 20, maxHeight: 100, overflow: 'auto',
            }}
          />
          <button
            onClick={() => send(input)}
            disabled={!input.trim() || chatLoading}
            aria-label="Send"
            style={{
              width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
              background: input.trim() && !chatLoading ? 'var(--text)' : 'var(--border)',
              border: 'none', cursor: input.trim() && !chatLoading ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'opacity .12s',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 10.5V1.5M6 1.5L2.5 5M6 1.5L9.5 5" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 6,
        }}>
          <button
            onClick={() => setNewSessionConfirming(true)}
            disabled={newSessionClosing}
            style={{
              fontSize: 11, color: 'var(--text-3)',
              background: 'none', border: 'none', padding: 0,
              cursor: 'pointer', lineHeight: 1,
            }}
          >New session</button>
          <span style={{ fontSize: 10, color: 'var(--text-4)' }}>Your data is private and secure</span>
        </div>
      </div>
    </div>
  );
}

// ─── Anxiety-reduction capture card (anxiety-reduction-brief.md) ──────────────
// Multi-prompt card rendered INLINE in the chat thread. Three open fields (each with its
// own mic), one pooled blob on submit. The fields exist to help the user think broadly —
// the actionable/held split is entirely the server's job, so all three feed one pool.
const HANDOVER_PROMPTS = [
  "What's nagging at you right now?",
  "Anything you've been meaning to do but haven't?",
  "Anything bigger you're carrying, with no clear next step?",
];

function AnxietyCaptureCard({ prefill, loading, onSubmit, onClose }) {
  const [fields, setFields] = useState(() => {
    const init = ['', '', ''];
    if (prefill) init[0] = prefill;
    return init;
  });

  const setField = (i, v) => setFields(f => f.map((x, j) => (j === i ? v : x)));
  const appendField = (i, chunk) => setFields(f => f.map((x, j) => (j === i ? (x ? x + ' ' + chunk : chunk) : x)));
  const pooled = fields.map(s => s.trim()).filter(Boolean).join('\n\n');
  const canSubmit = pooled.length > 0 && !loading;

  return (
    <div className="fade-in" style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '14px 14px 12px', marginTop: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Hand it over
        </span>
        <button
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
          style={{ background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer', color: 'var(--text-4)', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
        >×</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {HANDOVER_PROMPTS.map((q, i) => (
          <HandoverField
            key={i}
            question={q}
            value={fields[i]}
            disabled={loading}
            onChange={(v) => setField(i, v)}
            onDictate={(chunk) => appendField(i, chunk)}
          />
        ))}
      </div>

      <p style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--text-3)', margin: '10px 0 9px', lineHeight: 1.45 }}>
        This stays between us — I'll help sort through it.
      </p>

      <button
        onClick={() => onSubmit(pooled)}
        disabled={!canSubmit}
        style={{
          width: '100%', padding: '9px', borderRadius: 8, border: 'none',
          background: canSubmit ? 'var(--text)' : 'var(--border)',
          color: canSubmit ? 'var(--bg)' : 'var(--text-3)',
          fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
          cursor: canSubmit ? 'pointer' : 'default',
        }}
      >
        {loading ? 'Sorting…' : 'Hand this over'}
      </button>
    </div>
  );
}

function HandoverField({ question, value, disabled, onChange, onDictate }) {
  const { listening, supported, toggle } = useDictation(onDictate);
  return (
    <div>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-2)', margin: '0 0 4px', lineHeight: 1.4 }}>{question}</p>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 7,
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 9, padding: '7px 8px 7px 11px',
      }}>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={1}
          placeholder="Type or dictate…"
          style={{
            flex: 1, background: 'none', border: 'none', outline: 'none', resize: 'none',
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.5,
            minHeight: 20, maxHeight: 90, overflow: 'auto',
          }}
        />
        {supported && (
          <button
            onClick={toggle}
            disabled={disabled}
            aria-label={listening ? 'Stop dictation' : 'Dictate'}
            title={listening ? 'Listening… tap to stop' : 'Dictate'}
            style={{
              flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
              background: listening ? 'var(--danger)' : 'none',
              border: `1px solid ${listening ? 'var(--danger)' : 'var(--border)'}`,
              color: listening ? '#fff' : 'var(--text-3)',
              cursor: disabled ? 'default' : 'pointer', fontSize: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >{listening ? '●' : '🎙'}</button>
        )}
      </div>
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────────────────────────

function MessageBubble({ message, opener }) {
  const isUser = message.role === 'user';
  const [expanded, setExpanded] = useState(false);
  const full = (message.content || '').trim();

  // The opening brief is long for a chat opener — show the first ~3 sentences with a
  // "Show more" toggle.
  const sentences = full.match(/[^.!?]+[.!?]+(?:\s|$)/g);
  const head = sentences ? sentences.slice(0, 3).join('').trim() : full;
  const isLong = !!opener && !isUser && !!sentences && sentences.length > 3 && head.length < full.length;
  const display = isLong && !expanded ? head : full;

  return (
    <div
      style={{
        maxWidth: isUser ? '85%' : '100%',
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        padding: isUser ? '9px 13px' : 0,
        borderRadius: isUser ? '14px 14px 4px 14px' : 0,
        background: isUser ? '#2A2825' : 'none',
        color: isUser ? '#fff' : '#2A2825',
        fontFamily: 'var(--font-body)',
        fontSize: 14, lineHeight: 1.55,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}
    >
      {display}
      {isLong && (
        <span
          onClick={() => setExpanded(e => !e)}
          style={{ display: 'block', marginTop: 6, color: '#A8A49F', fontSize: 13, cursor: 'pointer' }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </span>
      )}
    </div>
  );
}

// ─── Typing Indicator ─────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div style={{
      alignSelf: 'flex-start',
      background: 'var(--bg)',
      borderRadius: '12px 12px 12px 3px',
      padding: '9px 13px',
      display: 'flex', gap: 4, alignItems: 'center',
    }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 5, height: 5, borderRadius: '50%', background: 'var(--text-4)',
          animation: `pulse 1.2s ease ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
  );
}

// ─── Page Loader ──────────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div style={{
      flex: 1, height: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-3)', fontSize: 14,
    }}>Loading…</div>
  );
}
